// Modal centrada "Subir varios productos" (diseño ATLAS). Dos fuentes, dos estilos:
//   • Archivos LOCALES (drag & drop / "Subir archivos") → lista con nombre editable
//     + tachito (igual que siempre). Lógica en el hook `useBulkUpload`.
//   • TIENDANUBE → grid de cuadrados SELECCIONABLES (sin editar nombre ni borrar):
//     elegís cuáles traer + "Seleccionar todos". Por defecto NINGUNO seleccionado.
// Al confirmar, crea los locales (hook) + los de Tiendanube ELEGIDOS (webhook n8n).

import React from "react";
import { createPortal } from "react-dom";
import {
  X,
  UploadCloud,
  Layers,
  Check,
  ChevronDown,
  ChevronLeft,
  Trash2,
  Loader2,
  Package,
  Info,
  ImagePlus,
  Plus,
  Sparkles,
  Combine,
  Pencil,
} from "lucide-react";
import { useBulkUpload } from "../hooks/useBulkUpload";
import { useExitTransition } from "../hooks/useExitTransition";
import { MAX_IMAGENES_POR_PRODUCTO } from "../lib/limiteImagenesProducto";
import { agruparImagenesConIa } from "../lib/agruparImagenesIa";
import { capturarCarpetasSoltadas } from "../lib/leerLoteDeProductos";
import { SparklesLoaderInline } from "./loading/SparklesLoader";
import { supabase } from "../lib/supabase";
import {
  ensureTnProducts,
  abrirVentanaOAuth,
  EVENTO_CAMBIO_DE_TIENDANUBE,
  getTnStatus,
  type TnProduct,
} from "../lib/tiendaNube";
import { importarProductosDeLaTienda } from "../lib/tiendanubeOperaciones";
import { traerCatalogoAlConectar } from "../lib/traerCatalogoAlConectar";
import { TiendaNubeIcon } from "./TiendaNubeIcon";
import { TiendaNubeConnectModal } from "./TiendaNubeConnectModal";
import { MercadoLibreImportModal } from "./MercadoLibreImportModal";
import { MercadoLibreConnectModal } from "./MercadoLibreConnectModal";
import {
  ErrorDeMercadoLibre,
  EVENTO_CAMBIO_DE_MERCADOLIBRE,
} from "../lib/mercadoLibreConexionLogica";
import {
  abrirVentanaDeMercadoLibre,
  conectarMercadoLibreEnVentana,
  estadoDeMercadoLibre,
} from "../lib/mercadoLibreConexion";
import {
  AsideDeAlta,
  BotonPrincipalDeAlta,
  ColumnaDeTrabajo,
  ColumnasDeAlta,
  CuerpoDeAlta,
  PieDeAlta,
  TipsDeAlta,
} from "../pages/v2/components/altaEnDosColumnas";
import { MercadoLibreIcon } from "./MercadoLibreIcon";
import { AbanicoDeFotos } from "./AbanicoDeFotos";
import { BotonCancelarDeAlta, BotonDeAlta, PieFijoDeAlta } from "./PieFijoDeAlta";

// Tope de productos de Tiendanube por import (acordado: ~50). El front los manda en
// tandas al backend, que los procesa con un worker por producto (el tamaño de la tanda
// vive en `lib/tiendanubeOperaciones`). Con el dedup del Paso 1, se importa en olas sin
// saturar.
const MAX_TN = 50;

// Duración de la animación de entrada/salida. Tiene que coincidir con las
// `duration-*` de las clases de abajo: `useExitTransition` desmonta a los
// EXIT_MS, y si fuera menor que la transition la salida se cortaría a la
// mitad. 300 ms — igual que el resto de los sheets de la app.
const EXIT_MS = 300;

interface Props {
  open: boolean;
  companyId: string | null;
  brandId: string | null;
  userPlan: string;
  onClose: () => void;
  /** Refresca la grilla de productos cuando se crean. */
  onSaved: () => Promise<void> | void;
  /** Altura de apilado del overlay. Default 80 (Mi Marca, donde no compite
   *  con nada). La Interfaz V2 lo abre POR ENCIMA de sus paneles, que viven
   *  entre z-100 (PresetDetailSheet) y z-211 (MultiSlotProductPickerSheet):
   *  sin esto el popup queda tapado por el panel que lo abrió (ADR-684).
   *  Va como estilo inline y no como clase `z-[N]`: Tailwind no compila
   *  clases armadas en runtime. */
  zIndex?: number;
  /**
   * Se dibuja SIN su ventana propia (sin portal, sin overlay y sin animación de
   * entrada): el contenido —cabecera, lista y pie— llena el contenedor del host
   * (ADR-2026-09-22-el-alta-en-bloque-tambien-adentro-del-selector). Lo usa el selector
   * de fuentes de la caja del Agente, que ya es una ventana flotante y lo muestra en su
   * cuerpo en vez de abrir una segunda encima. El host pone la salida (su flecha de
   * volver y su X), así que acá no se dibuja la X ni la pastilla de arrastre.
   *
   * `zIndex` sigue valiendo: es el del aviso de Tiendanube, que SÍ es una ventana y
   * tiene que quedar por encima de la del host.
   */
  embebido?: boolean;
  /**
   * Avisa cuándo hay una operación en curso (crear, subir, traer de Tiendanube, agrupar
   * con IA). Embebido, la salida la pone el host —su flecha de volver, su X— y sin esto
   * no puede bloquearla como este componente bloquea la suya: irse en medio de un
   * guardado desmonta la lista con las subidas en vuelo.
   */
  onOcupado?: (ocupado: boolean) => void;
  /**
   * Se dibuja ADENTRO del panel lateral de "Agregar producto", en el lugar de su contenido
   * (ADR-2026-10-06-subir-varios-adentro-del-panel): sin portal ni overlay, una sola
   * columna, con "Volver" arriba (que es `onClose`) y la X del panel (`onCerrarPanel`).
   */
  enPanel?: boolean;
  /** Con `enPanel`: cierra el panel entero (no sólo vuelve al alta de un producto). */
  onCerrarPanel?: () => void;
  /**
   * La tanda ENTERA se creó (locales y de la tienda). Va en lugar de `onClose` al terminar:
   * el host del panel cierra el panel acá y no en `onSaved`, que también llega cuando
   * algunos productos fallaron y tienen que quedar para reintentar
   * (ADR-2026-10-07-subir-varios-cierra-solo-si-salio-todo).
   */
  onTerminado?: () => void;
  /**
   * Se dibuja como parte de la pantalla "Subir productos" de Mi marca
   * (ADR-2026-10-13-subir-productos-es-una-pantalla): sin portal, sin ventana y sin
   * cabecera —el título, el volver y el selector "Un producto / Varios" son de la
   * pantalla—, y con la barra de "Crear" pegada abajo (`PieFijoDeAlta`). `onClose` es el
   * "Cancelar" de esa barra.
   */
  enPagina?: boolean;
  /**
   * Con `enPagina`: le pasa a la pantalla lo que necesita para dibujar "Crear N productos"
   * en su cabecera (escritorio) — ADR-2026-10-13-varios-productos-reestructurado. Se llama
   * sólo cuando algo de eso cambia (y con `null` al desmontar); `crear` es estable.
   */
  onAcciones?: (acciones: AccionesDeVarios | null) => void;
  /** Con `enPagina`: lo que va al final de la pantalla, antes de la barra fija del teléfono. */
  alFinal?: React.ReactNode;
  /**
   * El mismo armado que `enPagina`, pero adentro del pop-up de subir de los modos
   * (`ModalDeSubirProductos` — ADR-2026-10-14-subir-productos-en-un-pop-up-desde-modelo):
   * sin la barra de "Crear" de la página —los botones los pone el pie del pop-up, con
   * `onAcciones`— y con los avisos y el producto levantado siempre a la vista, pegados
   * abajo del scroll del pop-up.
   */
  enVentana?: boolean;
}

/** Lo que la pantalla necesita para dibujar el botón de crear del alta en bloque. */
export type AccionesDeVarios = {
  crear: () => void;
  puedeCrear: boolean;
  creando: boolean;
  etiqueta: string;
  /** Hay fotos o productos de la tienda elegidos: irse los pierde (no hay borrador). */
  hayCargado: boolean;
};

/** Las tarjetas blancas de arriba en la pantalla, iguales a las de "Un solo producto". */
const TARJETA_DE_PAGINA =
  "rounded-2xl bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_0_0_1px_rgba(16,24,40,0.06)] sm:px-5 sm:py-4";

export const AgregarVariosModal: React.FC<Props> = ({
  open,
  companyId,
  brandId,
  userPlan,
  onClose,
  onSaved,
  zIndex = 80,
  embebido = false,
  onOcupado,
  enPanel = false,
  onCerrarPanel,
  onTerminado,
  enPagina: enPaginaProp = false,
  onAcciones,
  alFinal,
  enVentana = false,
}) => {
  const enPagina = enPaginaProp || enVentana;
  const bulk = useBulkUpload({
    idCompany: companyId,
    userPlan,
    brandId,
    onSuccess: onSaved,
  });
  const [dragOver, setDragOver] = React.useState(false);
  // Entrada Y salida animadas. `useExitTransition` mantiene el componente
  // montado durante EXIT_MS después de que `open` pasa a false, para que la
  // CSS transition de salida alcance a correr antes del unmount (ADR-686).
  const { mounted, animated } = useExitTransition(open, EXIT_MS);
  // El estado NO se limpia al cerrar sino EXIT_MS después: si se limpiara al
  // toque, durante la salida se vería el sheet ya vacío (la lista de productos
  // desapareciendo antes que el sheet). Si el user reabre antes de que el
  // timer corra, lo cancelamos — si no, borraría la tanda nueva.
  const resetTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetAfterExit = () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      resetTimerRef.current = null;
      bulk.clearAll();
      setTnItems([]);
      setTnSelected(new Set());
      setError(null);
      setNotice(null);
      setActionKey(null);
      setPickedKey(null);
      setMergeError(null);
      setFlashKey(null);
      setGalleryKey(null);
      setAiNotice(null);
    }, EXIT_MS);
  };
  React.useEffect(() => {
    if (!open || !resetTimerRef.current) return;
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = null;
  }, [open]);
  React.useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    [],
  );
  // Mobile = bottom sheet (entra deslizando desde abajo); desktop = modal
  // centrada. Es por VIEWPORT, no por puntero: el layout tiene que seguir al
  // ancho de la ventana, así que reacciona al resize (ADR-685).
  const [isMobile, setIsMobile] = React.useState(
    () =>
      typeof window !== "undefined" &&
      !window.matchMedia("(min-width: 768px)").matches,
  );
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsMobile(!mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // El drag&drop de archivos no sirve en mobile/PWA (no se pueden arrastrar
  // archivos del SO) → ocultamos esa zona ahí y dejamos solo los botones.
  const [pointerSupportsDrop] = React.useState(
    () =>
      typeof window !== "undefined" &&
      !window.matchMedia("(pointer: coarse)").matches &&
      !window.matchMedia("(display-mode: standalone)").matches,
  );
  // La zona de arrastre se ata TAMBIÉN al viewport: en formato bottom sheet no
  // tiene sentido ofrecer "arrastrá tus imágenes acá" aunque el dispositivo
  // técnicamente tenga mouse (ventana de escritorio angosta). Un solo criterio
  // para la presentación y para el drop.
  const dropEnabled = pointerSupportsDrop && !isMobile;
  const [tnItems, setTnItems] = React.useState<TnProduct[]>([]);
  const [tnSelected, setTnSelected] = React.useState<Set<number>>(new Set());
  const [tnLoading, setTnLoading] = React.useState(false);
  // ¿La marca ya tiene Tiendanube conectado? Lo muestra el botón de la tarjeta destacada
  // ("Conectada" en vez del "+"). null = todavía no se sabe: se dibuja el "+", que es la
  // acción. Se relee al abrir y cada vez que alguien conecta o desconecta una tienda.
  const [tnConectada, setTnConectada] = React.useState<boolean | null>(null);
  // Sin tienda conectada, el botón abre primero la MISMA explicación que "Conectar" en Mi marca
  // (`TiendaNubeConnectModal`) — ADR-2026-10-22-subir-varios-conecta-con-la-misma-explicacion.
  const [tnExplicacion, setTnExplicacion] = React.useState(false);
  const [tnEmpresaConTiendas, setTnEmpresaConTiendas] = React.useState(false);
  // De QUÉ marca es lo que dice `tnConectada`. Con otra marca se vuelve a "no se sabe": si
  // no, el "Conectada" de la anterior quedaba hasta que llegara la respuesta, o para siempre
  // si fallaba. Reabrir con la MISMA marca no lo borra, así no parpadea "+" → "Conectada"
  // (review atlas-code#187, ADR-2026-10-17-review-187-el-sello-de-tiendanube-es-de-su-marca).
  const marcaDelSelloRef = React.useRef(brandId);
  React.useEffect(() => {
    if (!open) return;
    if (marcaDelSelloRef.current !== brandId) {
      marcaDelSelloRef.current = brandId;
      setTnConectada(null);
    }
    let vigente = true;
    const leer = () =>
      void getTnStatus(brandId).then((s) => {
        if (vigente && !s.error) {
          setTnConectada(s.connected);
          setTnEmpresaConTiendas(s.companyHasStores);
        }
      });
    leer();
    window.addEventListener(EVENTO_CAMBIO_DE_TIENDANUBE, leer);
    return () => {
      vigente = false;
      window.removeEventListener(EVENTO_CAMBIO_DE_TIENDANUBE, leer);
    };
  }, [open, brandId]);
  // Mercado Libre: modal propio (ADR-2026-09-28-mercadolibre-importar-por-el-atributo-de-la-foto, en
  // atlas-backend). Lo ve todo el mundo (ADR-2026-10-22-mercadolibre-sin-interruptores).
  const [mlOpen, setMlOpen] = React.useState(false);
  // ¿La marca tiene Mercado Libre conectado? Igual que `tnConectada`: el botón destacado muestra
  // "+" o "Conectada", y sin conexión el click conecta en vez de abrir un modal que sólo podía
  // decir que falta conectar (ADR-2026-10-22-mercadolibre-sin-conectar-no-es-un-error).
  // null = todavía no se sabe. `mlDisponible: false` = integración apagada: no hay botón.
  const [mlConectada, setMlConectada] = React.useState<boolean | null>(null);
  const [mlDisponible, setMlDisponible] = React.useState(true);
  const [mlConectando, setMlConectando] = React.useState(false);
  // Igual que Tiendanube: sin conexión, primero la explicación de Mi marca (`MercadoLibreConnectModal`).
  const [mlExplicacion, setMlExplicacion] = React.useState(false);
  const [mlReconectar, setMlReconectar] = React.useState(false);
  const [mlErrorDeConexion, setMlErrorDeConexion] = React.useState<string | null>(null);
  const marcaDelSelloMlRef = React.useRef(brandId);
  React.useEffect(() => {
    if (!open) return;
    if (marcaDelSelloMlRef.current !== brandId) {
      marcaDelSelloMlRef.current = brandId;
      setMlConectada(null);
    }
    let vigente = true;
    const leer = () =>
      void estadoDeMercadoLibre(brandId).then((e) => {
        if (!vigente || e.error) return;
        setMlDisponible(e.disponible);
        setMlConectada(e.conectada && e.estado !== "reconectar");
        setMlReconectar(e.estado === "reconectar");
      });
    leer();
    window.addEventListener(EVENTO_CAMBIO_DE_MERCADOLIBRE, leer);
    return () => {
      vigente = false;
      window.removeEventListener(EVENTO_CAMBIO_DE_MERCADOLIBRE, leer);
    };
  }, [open, brandId]);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  // Aviso "vas a conectar otra tienda" (solo si la empresa ya tiene una).
  const [warnOpen, setWarnOpen] = React.useState(false);
  const warnResolveRef = React.useRef<((v: boolean) => void) | null>(null);
  // Drag-to-select de los productos de Tiendanube: mantener apretado + arrastrar
  // selecciona varios. Sin "modo" (la grilla ya es siempre seleccionable).
  const longPressTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const longPressTriggeredRef = React.useRef(false);
  const dragSelectRef = React.useRef<{
    active: boolean;
    toggleTo: "select" | "deselect";
    visited: Set<number>;
    startX: number;
    startY: number;
    hasMoved: boolean;
  } | null>(null);
  // ── "Añadir a otro producto" ──────────────────────────────────────────
  // Tocar una fila despliega la opción; elegirla "levanta" la unidad (sale
  // de la lista y queda flotante abajo); tocar otra fila la fusiona ahí.
  // Estado puramente de UI — el merge real vive en el hook (mergeUnits).
  const [actionKey, setActionKey] = React.useState<string | null>(null);
  const [pickedKey, setPickedKey] = React.useState<string | null>(null);
  const [mergeError, setMergeError] = React.useState<string | null>(null);
  // Fila con la galería abierta (tap en el thumbnail): el nombre se oculta y
  // se ven las demás imágenes del producto + un "+" para añadir otra.
  const [galleryKey, setGalleryKey] = React.useState<string | null>(null);
  // "Agrupar con IA": spinner mientras el backend agrupa, y aviso con
  // "Deshacer" después de aplicar.
  const [aiWorking, setAiWorking] = React.useState(false);
  const [aiNotice, setAiNotice] = React.useState<string | null>(null);
  // Input propio para el "+" de una fila (el del hook es el picker general).
  // La key destino viaja por ref: el onChange llega después del click.
  const addToUnitInputRef = React.useRef<HTMLInputElement>(null);
  const addToUnitKeyRef = React.useRef<string | null>(null);
  // Unidad que acaba de recibir imágenes → flash breve de confirmación.
  const [flashKey, setFlashKey] = React.useState<string | null>(null);
  const flashTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  React.useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    [],
  );
  // Si la unidad levantada desaparece por otro camino (se borró, cambió el
  // modo de agrupamiento), el flotante no puede quedar apuntando a nada.
  React.useEffect(() => {
    if (pickedKey && !bulk.units.some((u) => u.key === pickedKey)) {
      setPickedKey(null);
      setMergeError(null);
    }
  }, [pickedKey, bulk.units]);
  // Tocar FUERA de la fila con la galería abierta la colapsa. Los taps
  // dentro se reconocen por el atributo data-galeria-abierta de la fila
  // (y los controles internos además cortan la propagación, así el toggle
  // del thumbnail no llega a este listener y no se pisa con él).
  // ⚠️ Tiene que vivir ANTES del `if (!mounted) return null`: un hook después
  // de ese early-return aparece "de más" al abrir el modal y React revienta
  // con "Rendered more hooks than during the previous render".
  React.useEffect(() => {
    if (!galleryKey) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest("[data-galeria-abierta]")) return;
      setGalleryKey(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [galleryKey]);

  // Listener global del arrastre: detecta las cards bajo el dedo/mouse
  // (`elementFromPoint` + `[data-tn-id]`) y las selecciona. Threshold para no
  // confundir con scroll; preventDefault para frenar el scroll mientras arrastra.
  React.useEffect(() => {
    if (!open) return;
    const applyToCard = (id: number, toggleTo: "select" | "deselect") =>
      setTnSelected((prev) => {
        const next = new Set(prev);
        if (toggleTo === "select") {
          if (next.has(id) || next.size >= MAX_TN) return prev;
          next.add(id);
        } else {
          if (!next.has(id)) return prev;
          next.delete(id);
        }
        return next;
      });
    const cardIdAt = (x: number, y: number): number | null => {
      const card = document
        .elementFromPoint(x, y)
        ?.closest("[data-tn-id]");
      const attr = card?.getAttribute("data-tn-id");
      return attr ? Number(attr) : null;
    };
    const move = (
      x: number,
      y: number,
      threshold: number,
      prevent: () => void,
    ) => {
      const st = dragSelectRef.current;
      if (!st) return;
      if (!st.active && Math.hypot(x - st.startX, y - st.startY) > threshold) {
        st.active = true;
        st.hasMoved = true;
        const first = st.visited.values().next().value as number | undefined;
        if (first != null) applyToCard(first, st.toggleTo);
      }
      if (st.active) {
        prevent();
        const id = cardIdAt(x, y);
        if (id != null && !st.visited.has(id)) {
          st.visited.add(id);
          applyToCard(id, st.toggleTo);
        }
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t)
        move(t.clientX, t.clientY, 18, () => {
          if (e.cancelable) e.preventDefault();
        });
    };
    const onMouseMove = (e: MouseEvent) =>
      move(e.clientX, e.clientY, 8, () => e.preventDefault());
    const onEnd = () => {
      const st = dragSelectRef.current;
      if (st?.hasMoved) longPressTriggeredRef.current = true; // suprime el click
      dragSelectRef.current = null;
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onEnd);
    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onEnd);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, [open]);

  // aiWorking cuenta como ocupado: crear/cerrar/fusionar mientras el backend
  // agrupa correría una carrera contra el reagrupado que está por llegar.
  const ocupado = creating || bulk.saving || tnLoading || aiWorking;
  // El host se entera (ver `onOcupado`). El `false` del cleanup cubre el desmontaje:
  // si no, quien nos hospeda se queda creyendo que seguimos guardando.
  React.useEffect(() => {
    onOcupado?.(ocupado);
    return () => onOcupado?.(false);
  }, [ocupado, onOcupado]);

  // Las acciones para la cabecera de la pantalla (`onAcciones`). Se calculan más abajo, en
  // el render (después del `return` temprano), y se mandan acá sólo si cambiaron: el host
  // guarda el objeto en su estado, y mandarlo en cada render sería un bucle.
  const crearRef = React.useRef<() => void>(() => {});
  const accionesRef = React.useRef<AccionesDeVarios | null>(null);
  const ultimaClaveRef = React.useRef<string>("");
  const crearEstable = React.useCallback(() => crearRef.current(), []);
  React.useEffect(() => {
    if (!enPagina || !onAcciones || !accionesRef.current) return;
    const a = accionesRef.current;
    const clave = `${a.puedeCrear}|${a.creando}|${a.etiqueta}|${a.hayCargado}`;
    if (clave === ultimaClaveRef.current) return;
    ultimaClaveRef.current = clave;
    onAcciones(a);
  });
  // Al limpiar se olvida la última clave: si no, cuando el efecto vuelve a montarse (Strict
  // Mode lo hace siempre en desarrollo) la clave es la misma, no se vuelve a mandar nada y
  // la cabecera se queda sin botones hasta que se carga algo.
  React.useEffect(
    () => () => {
      ultimaClaveRef.current = "";
      onAcciones?.(null);
    },
    [onAcciones],
  );

  if (!mounted) return null;

  const busy = ocupado;
  // Productos locales = unidades (base por modo + merges manuales): es lo
  // que saveAll va a crear, no la cantidad de archivos.
  const localCount = bulk.units.length;
  const total = localCount + tnSelected.size;
  const creatable = localCount > 0 || tnSelected.size > 0;

  // Embebido (el selector de fuentes, 980 de ancho) y con imágenes ya cargadas, la zona
  // de fuentes se ACHICA a un renglón: lo que importa a esa altura es la lista de
  // productos, y un recuadro punteado de 180 px arriba la empujaba abajo del pliegue
  // (ADR-2026-09-24-el-alta-en-bloque-tambien-en-dos-columnas).
  const fuentesCompactas = (embebido || enPanel || enPagina) && bulk.items.length > 0;
  // Los avisos y el producto levantado traen la sangría de la ventana (24px). En la
  // pantalla de Mi marca van en la barra de abajo, que ya está alineada con la columna.
  const sangria = enPagina ? "" : "px-6";
  // "Seleccionar todos" selecciona hasta el tope (no más de MAX_TN).
  const selectableTn = Math.min(tnItems.length, MAX_TN);
  const allSelected = selectableTn > 0 && tnSelected.size === selectableTn;

  const handleClose = () => {
    if (busy) return;
    onClose();
    resetAfterExit();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    // Una carpeta soltada (escritorio) arma un producto por carpeta, como un .zip
    // (ADR-2026-10-16-subir-varios-con-zip-y-carpetas). Se captura ACÁ, antes de
    // cualquier await: después el dataTransfer ya no se puede leer.
    const leerCarpetas = e.dataTransfer ? capturarCarpetasSoltadas(e.dataTransfer) : null;
    if (leerCarpetas || e.dataTransfer?.files?.length) {
      // Acción nueva → el error local viejo (guardado/TN) deja de aplicar;
      // sin esto taparía el bulk.error del archivo recién salteado.
      setError(null);
      if (leerCarpetas) void bulk.addCarpetas(leerCarpetas);
      else void bulk.addFiles(e.dataTransfer.files);
    }
  };

  /**
   * Mercado Libre: conectada (o sin saber todavía) → el modal para elegir publicaciones; sin
   * conectar → la conexión, y al volver, el modal. La ventana se abre ACÁ, antes de cualquier
   * `await` (Safari la bloquea si no: ADR-2026-08-07-tiendanube-popup-safari).
   */
  const handleTraerMercadoLibre = () => {
    if (busy || mlConectando) return;
    if (mlConectada === false) {
      setMlErrorDeConexion(null);
      setMlExplicacion(true);
      return;
    }
    setMlOpen(true);
  };

  /** "Conectar" de la explicación: conecta y, al volver, abre la lista para elegir publicaciones. */
  const conectarMercadoLibre = async () => {
    if (mlConectando) return;
    // La ventana se abre ACÁ, antes de cualquier `await` (Safari: ADR-2026-08-07-tiendanube-popup-safari).
    const popup = abrirVentanaDeMercadoLibre();
    setMlErrorDeConexion(null);
    setMlConectando(true);
    try {
      await conectarMercadoLibreEnVentana(brandId, popup);
      setMlConectada(true);
      setMlExplicacion(false);
      setMlOpen(true);
    } catch (e) {
      // Cerrar la ventana a propósito no es un error (mismo criterio que Mi marca).
      if (e instanceof ErrorDeMercadoLibre && e.code === "ventana_cerrada") setMlErrorDeConexion(null);
      else setMlErrorDeConexion(e instanceof Error ? e.message : "No se pudo conectar.");
    } finally {
      setMlConectando(false);
    }
  };

  /** Sin tienda conectada, primero la explicación; con tienda (o sin saber), lo de siempre. */
  const handleBotonTiendaNube = () => {
    if (busy) return;
    if (tnConectada === false) {
      setTnExplicacion(true);
      return;
    }
    void handleTraerTiendaNube();
  };

  /** `desdeExplicacion`: la explicación ya avisó si la empresa tiene otra tienda; no se pregunta dos veces. */
  const handleTraerTiendaNube = async (desdeExplicacion = false) => {
    if (busy) return;
    // ⚠️ La ventana del OAuth se abre ACÁ, sincrónicamente dentro del click y
    // ANTES de cualquier `await`: Safari (y todos los navegadores de iOS) la
    // bloquean si se la pide después — ver ADR-2026-08-07-tiendanube-popup-safari.
    // Este flujo consulta los productos antes de saber si hace falta conectar,
    // así que la abre por las dudas; `ensureTnProducts` la cierra sola si la
    // tienda ya estaba conectada.
    const popup = abrirVentanaOAuth();
    setError(null);
    setNotice(null);
    setTnLoading(true);
    try {
      const { products, conecto } = await ensureTnProducts(brandId, {
        popup,
        confirmNewStore: desdeExplicacion
          ? () => Promise.resolve(true)
          : () =>
              new Promise<boolean>((resolve) => {
                warnResolveRef.current = resolve;
                setWarnOpen(true);
              }),
      });
      // Recién conectada: se trae el catálogo entero, como cuando se conecta desde
      // Tiendanube (ADR-2026-10-01-conectar-desde-atlas-trae-el-catalogo). La grilla
      // para elegir queda para quien ya tenía la tienda y vuelve por lo nuevo.
      if (conecto) {
        setTnConectada(true);
        // Le pasa la lista que ya leímos para no pedir el catálogo dos veces; si vino
        // vacía (puede ser timing de Tiendanube recién conectada), relee con reintentos.
        const { enviados, fallo, sinMandar, masiva } = await traerCatalogoAlConectar(
          brandId,
          products,
        );
        const trayendo = `estamos trayendo ${enviados === 1 ? "tu producto" : `tus ${enviados} productos`}. Van a aparecer en un ratito.`;
        // Los que no salieron quedan en la grilla, tildados, para reintentar SÓLO esos
        // con "Crear productos". Mandarlo a "Traer de Tiendanube" releería la tienda, que
        // listaría también los que siguen en la cola del worker, y se duplicarían
        // (review Greptile #154).
        if (sinMandar.length > 0) {
          setTnItems((prev) => {
            const seen = new Set(prev.map((p) => p.id));
            return [...prev, ...sinMandar.filter((p) => !seen.has(p.id))];
          });
          setTnSelected(new Set(sinMandar.slice(0, MAX_TN).map((p) => p.id)));
        }
        const reintentar = `Quedaron abajo, tildados: tocá "Crear productos" para reintentar.`;
        if (masiva) {
          // La importación masiva trae todo de a tandas: el avance se ve en Mi marca, en el botón de refresh.
          setNotice("Conectamos tu tienda y estamos trayendo tus productos. Van a ir apareciendo en Mi marca en un ratito.");
        } else if (enviados > 0 && !fallo) {
          setNotice(`Conectamos tu tienda y ${trayendo}`);
        } else if (enviados > 0) {
          setNotice(
            `Conectamos tu tienda y ${trayendo} ${sinMandar.length} no se pudieron mandar. ${reintentar}`,
          );
        } else if (sinMandar.length > 0) {
          setError(`Conectamos tu tienda, pero no pudimos traer tus productos. ${reintentar}`);
        } else if (fallo) {
          // Tiró antes de mandar nada (no hay cola que duplicar): releer es seguro.
          setError(
            'Conectamos tu tienda, pero no pudimos leer tus productos. Probá de nuevo con "Traer de Tiendanube".',
          );
        } else {
          setNotice("Conectamos tu tienda. No tiene productos nuevos con foto para traer.");
        }
        if (enviados > 0) await onSaved();
        return;
      }
      if (products.length === 0) {
        setNotice("No hay productos nuevos con foto para traer de tu tienda.");
        return;
      }
      // Sumar a la grilla, sin duplicar por id (los nuevos vienen sin seleccionar).
      setTnItems((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...products.filter((p) => !seen.has(p.id))];
      });
    } catch (e) {
      if (e instanceof Error && e.message === "CANCELLED") return; // canceló el aviso
      setError(e instanceof Error ? e.message : "No se pudo traer la tienda.");
    } finally {
      setTnLoading(false);
    }
  };

  const toggleTn = (id: number) =>
    setTnSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_TN) next.add(id); // no pasar del tope
      return next;
    });

  const toggleAll = () =>
    setTnSelected(
      allSelected
        ? new Set()
        : new Set(tnItems.slice(0, MAX_TN).map((t) => t.id)), // hasta el tope
    );

  // Inicio del gesto sobre una card. Mouse: trackea ya (arrastrar = seleccionar,
  // click = toggle). Touch: arma tras "mantener apretado" 350ms (para NO romper
  // el scroll de la grilla). El movimiento lo maneja el listener global.
  const startTnDrag = (id: number, x: number, y: number, isTouch: boolean) => {
    const toggleTo: "select" | "deselect" = tnSelected.has(id)
      ? "deselect"
      : "select";
    const arm = () => {
      dragSelectRef.current = {
        active: false,
        toggleTo,
        visited: new Set([id]),
        startX: x,
        startY: y,
        hasMoved: false,
      };
    };
    if (isTouch) {
      longPressTriggeredRef.current = false;
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = setTimeout(() => {
        arm();
        if (navigator.vibrate) navigator.vibrate(30); // feedback "ya podés arrastrar"
      }, 350);
    } else {
      arm();
    }
  };
  // Si el dedo se mueve antes de armar (scroll), cancelamos el "mantener apretado".
  const cancelTnLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  // Click de una card: si vino de un arrastre lo ignoramos; si no, toggle normal.
  const tnCardClick = (id: number) => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    if (!busy) toggleTn(id);
  };

  // Tap sobre una fila de la lista local: solo actúa con una unidad
  // levantada — la fila tocada es el DESTINO del merge. Sin pickup, la fila
  // no reacciona al tap: el nombre se edita tocándolo directo y la opción
  // "Añadir a otro producto" se despliega con el chevron de la fila.
  const handleRowTap = (key: string) => {
    if (busy || !pickedKey || key === pickedKey) return;
    const err = bulk.mergeUnits(pickedKey, key);
    if (err) {
      setMergeError(err);
      return;
    }
    setMergeError(null);
    setPickedKey(null);
    setFlashKey(key);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashKey(null), 600);
  };

  // Chevron de una fila: despliega/pliega su "Añadir a otro producto".
  const handleToggleAction = (key: string) => {
    if (busy) return;
    setActionKey((k) => (k === key ? null : key));
  };

  const handlePickUp = (key: string) => {
    setActionKey(null);
    setMergeError(null);
    // Las galerías se cierran: con un movimiento activo, la fila entera es
    // destino del merge y el "+" no debe interceptar ese tap.
    setGalleryKey(null);
    setPickedKey(key);
  };

  // Tap en el thumbnail de una fila (solo sin movimiento activo): alterna la
  // galería de esa fila.
  const handleThumbTap = (key: string) => {
    if (busy || pickedKey) return;
    setGalleryKey((k) => (k === key ? null : key));
  };

  // "+" de la galería: abre el picker y recuerda a qué unidad va lo elegido.
  const handleAddImageTo = (key: string) => {
    if (busy) return;
    addToUnitKeyRef.current = key;
    addToUnitInputRef.current?.click();
  };

  // "Agrupar con IA": manda thumbnails al backend y aplica los grupos sobre
  // attachTo (modo plano). Los estados de interacción se resetean antes: el
  // reagrupado cambia las keys de las unidades y dejarían de apuntar a nada.
  const handleAgruparIa = async () => {
    if (busy || bulk.itemsAgrupables.length < 2) return;
    setError(null);
    setNotice(null);
    setAiNotice(null);
    setActionKey(null);
    setPickedKey(null);
    setMergeError(null);
    setGalleryKey(null);
    setAiWorking(true);
    try {
      // Sólo las sueltas: las fotos de una carpeta ya vienen agrupadas.
      const grupos = await agruparImagenesConIa(
        bulk.itemsAgrupables,
        AbortSignal.timeout(180_000),
      );
      const productos = bulk.applyAiGrouping(grupos);
      setAiNotice(
        `La IA agrupó tus imágenes en ${productos} producto${productos === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : "No pudimos agrupar las imágenes.",
      );
    } finally {
      setAiWorking(false);
    }
  };

  const handleDeshacerIa = () => {
    if (busy) return;
    bulk.undoAiGrouping();
    setAiNotice(null);
  };

  const handleCancelPickup = () => {
    setPickedKey(null);
    setMergeError(null);
  };

  const handleCreate = async () => {
    if (busy || !creatable) return;
    // Una unidad levantada sin fusionar vuelve a la lista y se crea como su
    // propio producto (nunca se fusionó): que se vea ANTES de guardar.
    setActionKey(null);
    setPickedKey(null);
    setMergeError(null);
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      let localOk = true;
      if (bulk.items.length > 0) {
        const r = await bulk.saveAll();
        localOk = r === "all-success" || r === "none";
      }
      const selected = tnItems.filter((t) => tnSelected.has(t.id));
      let tnSinMandar = 0;
      if (selected.length > 0) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        // El armado del payload y las tandas viven en `lib/tiendanubeOperaciones`
        // (los usa también el agente); acá queda sólo qué se eligió en la grilla.
        const { enviados } = await importarProductosDeLaTienda(selected, {
          userId: user?.id,
          companyId,
          brandId,
        });
        // Los que salieron se sacan de la grilla: siguen en la cola del worker, que no
        // deduplica, y un reintento los crearía dos veces. Son los primeros `enviados`
        // porque el import corta en la primera tanda rechazada (review Greptile #154).
        const salieron = new Set(selected.slice(0, enviados).map((t) => t.id));
        setTnItems((prev) => prev.filter((t) => !salieron.has(t.id)));
        setTnSelected((prev) => new Set([...prev].filter((id) => !salieron.has(id))));
        tnSinMandar = selected.length - enviados;
        if (enviados > 0) {
          await new Promise((r) => setTimeout(r, 3500));
          await onSaved();
        }
      }
      if (localOk && tnSinMandar === 0) {
        // Mismo criterio que handleClose: cerrar primero y limpiar después de
        // la animación, para no ver la lista vaciarse mientras el sheet sale.
        (onTerminado ?? onClose)();
        resetAfterExit();
      } else if (tnSinMandar > 0) {
        // Lo local que ya se creó entero no puede quedar en la lista: el reintento lo
        // volvería a crear (con "partial", `saveAll` ya sacó los que salieron).
        if (localOk) bulk.clearAll();
        const tienda = `No pudimos traer ${tnSinMandar === 1 ? "1 producto" : `${tnSinMandar} productos`} de tu tienda. Quedaron tildados: tocá "Crear productos" para reintentar.`;
        setError(localOk ? tienda : `${bulk.error || "Algunos productos fallaron."} ${tienda}`);
      } else {
        setError(
          bulk.error || "Algunos productos fallaron — revisalos e intentá de nuevo.",
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hubo un error.");
    } finally {
      setCreating(false);
    }
  };

  // Las PIEZAS del cuerpo, cada una en su constante: la ventana propia las apila en una
  // columna y el selector de fuentes las reparte en dos columnas
  // (ADR-2026-09-24-el-alta-en-bloque-tambien-en-dos-columnas). Son las MISMAS en los
  // dos casos: acá no hay una segunda versión de nada.

  const inputsOcultos = (
    <>
      {/* Input oculto del hook (file picker) */}
      <input
        ref={bulk.fileInputRef}
        type="file"
        // .zip: una carpeta por producto (ADR-2026-10-16-subir-varios-con-zip-y-carpetas).
        // PDF del proveedor: un producto por PDF (ADR-2026-10-18-el-pdf-entra-por-la-subida-de-siempre).
        accept="image/*,.zip,application/zip,application/pdf,.pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          // Igual que en onDrop: acción nueva limpia el error local viejo,
          // para que un bulk.error fresco no quede tapado por él.
          setError(null);
          bulk.handleSelectFiles(e);
        }}
      />
      {/* Input oculto del "+" de una fila: lo elegido se añade a la unidad
          que guardó handleAddImageTo (no crea productos nuevos). */}
      <input
        ref={addToUnitInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={async (e) => {
          setError(null);
          const key = addToUnitKeyRef.current;
          const files = e.target.files;
          if (key && files?.length) await bulk.addFilesToUnit(key, files);
          if (addToUnitInputRef.current) addToUnitInputRef.current.value = "";
        }}
      />
    </>
  );

  // Estilo de Mi marca (ADR-2026-10-06-subir-varios-con-la-cara-de-mi-marca): la zona es la
  // misma tarjeta punteada con el abanico que el alta de un producto (`AbanicoDeFotos`).
  // Tocarla abre el selector; en escritorio también se sueltan archivos encima.
  const botonDeTienda =
    "inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-black/[0.08] bg-white px-4 text-[14px] font-medium text-[#16181D] shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors hover:border-black/[0.16] disabled:opacity-60";
  // En la tarjeta destacada de la pantalla, Tiendanube va BLANCO con la acción en una
  // píldora a la derecha: un "+" sobre azul, o "Conectada" si la tienda ya está. La píldora
  // (32 px) va a 6 px del borde de un botón de 44: radios concéntricos, los dos rounded-full.
  const botonDeTiendaDestacado =
    "inline-flex h-11 items-center justify-center gap-2.5 whitespace-nowrap rounded-full border border-black/[0.08] bg-white pl-5 pr-1.5 text-[14.5px] font-semibold text-brand-black shadow-[0_8px_20px_-10px_rgba(22,68,235,0.45)] transition-[border-color,box-shadow] hover:border-[rgb(var(--brand-primary-rgb)_/_0.4)] hover:shadow-[0_10px_24px_-10px_rgba(22,68,235,0.55)] disabled:opacity-60";
  const tiendas = (destacadas = false) => (
    <>
      <button
        type="button"
        onClick={handleBotonTiendaNube}
        disabled={busy}
        aria-label={tnConectada ? "Traer productos de Tiendanube (conectada)" : "Traer productos de Tiendanube"}
        className={destacadas ? botonDeTiendaDestacado : botonDeTienda}
      >
        {tnLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <TiendaNubeIcon className="h-4 w-auto text-[#2c3357]" />
        )}
        {tnLoading ? "Conectando…" : "Tiendanube"}
        {destacadas &&
          !tnLoading &&
          (tnConectada ? (
            <span className="inline-flex h-8 items-center gap-1 rounded-full bg-emerald-50 px-3 text-[12.5px] font-semibold text-emerald-700">
              <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
              Conectada
            </span>
          ) : (
            <span aria-hidden className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-white">
              <Plus className="h-4 w-4" strokeWidth={2.5} />
            </span>
          ))}
      </button>
      {mlDisponible && (
        <button
          type="button"
          onClick={handleTraerMercadoLibre}
          disabled={busy || mlConectando}
          aria-label={mlConectada ? "Traer productos de Mercado Libre (conectada)" : "Traer productos de Mercado Libre"}
          className={destacadas ? botonDeTiendaDestacado : botonDeTienda}
        >
          {mlConectando ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MercadoLibreIcon className="h-4 w-4" />
          )}
          {mlConectando ? "Conectando…" : "Mercado Libre"}
          {destacadas &&
            !mlConectando &&
            (mlConectada ? (
              <span className="inline-flex h-8 items-center gap-1 rounded-full bg-emerald-50 px-3 text-[12.5px] font-semibold text-emerald-700">
                <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                Conectada
              </span>
            ) : (
              <span aria-hidden className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-white">
                <Plus className="h-4 w-4" strokeWidth={2.5} />
              </span>
            ))}
        </button>
      )}
    </>
  );
  const soltar = dropEnabled
    ? {
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          setDragOver(true);
        },
        onDragLeave: () => setDragOver(false),
        onDrop,
      }
    : {};

  const zonaDeFuentes = fuentesCompactas ? (
    // Compacta (embebido, con productos cargados): un renglón que dice qué se puede soltar
    // y los botones al lado, en vez de debajo de un recuadro alto.
    <div
      {...soltar}
      className={`rounded-2xl border-[1.5px] border-dashed px-4 py-3 transition-colors ${
        enPanel ? "" : "sm:flex sm:items-center sm:justify-between sm:gap-3"
      } ${
        dragOver ? "border-brand-primary bg-[rgb(var(--brand-primary-rgb)_/_0.085)]" : "border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-white"
      }`}
    >
      <span className={`mb-2 flex min-w-0 items-center gap-2 text-[13px] text-black/50 ${enPanel ? "mb-2.5" : "sm:mb-0"}`}>
        <UploadCloud className="h-4 w-4 shrink-0 text-[rgb(var(--brand-primary-rgb)_/_0.5)]" />
        <span className="truncate">
          {bulk.leyendoPdf
            ? `Leyendo «${bulk.leyendoPdf}»… (unos 15 segundos)`
            : dropEnabled
              ? "Arrastrá más fotos, carpetas o un .zip"
              : "Sumá más fotos o un .zip"}
        </span>
      </span>
      <div className={enPanel ? "flex flex-wrap gap-2" : "flex flex-col gap-2 sm:shrink-0 sm:flex-row sm:items-center"}>
        <button
          type="button"
          onClick={bulk.openFilePicker}
          disabled={busy || bulk.convertingHeic}
          className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-brand-primary px-4 text-[14px] font-medium text-white transition-colors hover:bg-brand-primary-hover disabled:opacity-60"
        >
          <Layers className="h-4 w-4" />
          Subir archivos
        </button>
        {/* En la pantalla, las tiendas tienen su tarjeta arriba. */}
        {!enPagina && tiendas()}
      </div>
    </div>
  ) : (
    <>
      <button
        type="button"
        onClick={bulk.openFilePicker}
        disabled={busy || bulk.convertingHeic}
        {...soltar}
        className={`group relative flex w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl border-[1.5px] border-dashed px-5 py-8 text-center transition-[border-color,box-shadow,background-color] duration-300 disabled:cursor-not-allowed ${
          embebido ? "md:flex-1" : ""
        } ${enPagina ? "sm:min-h-[340px]" : ""} ${
          dragOver
            ? "border-brand-primary bg-[rgb(var(--brand-primary-rgb)_/_0.085)] shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)]"
            : "border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-white to-[rgb(var(--brand-primary-rgb)_/_0.055)] hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)]"
        }`}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgb(var(--brand-primary-rgb)_/_0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
        />
        <AbanicoDeFotos cargando={bulk.convertingHeic} />
        <span className="relative">
          <span className="block text-[15px] font-semibold leading-tight text-[#16181D]">
            {bulk.leyendoPdf
              ? `Leyendo «${bulk.leyendoPdf}»…`
              : bulk.convertingHeic
                ? "Preparando las fotos…"
                : dropEnabled
                  ? "Arrastrá tus fotos acá"
                  : "Elegí las fotos de tus productos"}
          </span>
          <span className="mt-1 block text-[13px] leading-snug text-black/45">
            {dropEnabled ? "o hacé clic para elegirlas" : "Tocá para elegirlas"}
            {" · cada foto es un producto"}
          </span>
          <span className="mt-1 block text-[12.5px] leading-snug text-black/40">
            {dropEnabled
              ? "¿Varias fotos por producto? Soltá carpetas o un .zip: cada carpeta es un producto con su nombre."
              : "¿Varias fotos por producto? Subí un .zip: cada carpeta es un producto con su nombre."}
          </span>
        </span>
      </button>
      {!enPagina && (
        <>
          <div className="mt-5 flex items-center gap-3 text-[12.5px] text-black/40">
            <span className="h-px flex-1 bg-black/[0.08]" />
            o traé tus productos de
            <span className="h-px flex-1 bg-black/[0.08]" />
          </div>
          <div className={`mt-3 grid gap-2 grid-cols-1 sm:grid-cols-2`}>
            {tiendas()}
          </div>
        </>
      )}
    </>
  );

  const opcionesDelLote = (
    <>
      {/* ── Agrupar imágenes con IA: arma los productos juntando las
          fotos del mismo ítem y sugiere nombres. Solo con 2+ imágenes
          (con una no hay nada que agrupar), y se oculta mientras el
          aviso con "Deshacer" está a la vista — repetirlo ahí es
          redundante. ── */}
      {bulk.itemsAgrupables.length > 1 && !aiNotice && (
        <button
          type="button"
          onClick={handleAgruparIa}
          disabled={busy}
          // Mientras agrupa NO se atenúa (es el protagonista del
          // estado); el disabled:opacity queda para cuando lo
          // deshabilita otro busy (guardando, trayendo TN…).
          className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-white py-2.5 text-[14px] font-medium text-brand-primary transition-[border-color,box-shadow] hover:border-brand-primary hover:shadow-[0_10px_24px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] ${
            aiWorking ? "" : "disabled:opacity-50"
          }`}
        >
          {/* Caja fija de 20px para el ícono: el loader (sparkles de
              la pantalla de carga, en miniatura) y la estrellita
              estática miden distinto y sin esto el botón "salta" de
              alto al cambiar de estado. */}
          <span className="inline-flex w-5 h-5 items-center justify-center shrink-0">
            {aiWorking ? (
              <SparklesLoaderInline className="size-5 text-brand-primary" />
            ) : (
              <Sparkles className="w-4 h-4 text-brand-primary" />
            )}
          </span>
          {aiWorking ? "Agrupando imágenes…" : "Agrupar imágenes con IA"}
        </button>
      )}
      {aiNotice && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-[rgb(var(--brand-primary-rgb)_/_0.05)] px-3.5 py-2.5">
          <span className="text-[12.5px] text-black/60">
            {aiNotice} Revisá y corregí lo que quieras antes de crear.
          </span>
          <button
            type="button"
            onClick={handleDeshacerIa}
            disabled={busy}
            className="shrink-0 text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-opacity disabled:opacity-40"
          >
            Deshacer
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => bulk.setUseFileNames(!bulk.useFileNames)}
        className="mt-2 w-full flex items-center justify-between rounded-2xl border border-black/[0.08] bg-white px-4 py-2.5 transition-colors hover:border-black/[0.14]"
      >
        <span className="text-[13.5px] font-medium text-[#16181D]">
          Usar nombres de archivo
        </span>
        <span
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${bulk.useFileNames ? "bg-brand-primary" : "bg-neutral-300"}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${bulk.useFileNames ? "translate-x-4" : "translate-x-0.5"}`}
          />
        </span>
      </button>
    </>
  );

  const listaDeUnidades = (
    <>
      {/* Lista unificada de unidades (1 producto = 1+ imágenes).
          La fila levantada se colapsa con una transición de altura
          (grid-template-rows 1fr→0fr) mientras vive en el flotante
          de abajo; el espaciado va como pt-2 DENTRO del colapso para
          que el hueco se cierre junto con la fila. */}
      <div className="mt-1">
        {bulk.units.map((u, idx) => {
          const hidden = pickedKey === u.key;
          return (
            <div
              key={u.key}
              className="grid transition-all duration-300 ease-out"
              style={{
                gridTemplateRows: hidden ? "0fr" : "1fr",
                opacity: hidden ? 0 : 1,
              }}
            >
              <div
                className={`min-h-0 overflow-hidden ${
                  hidden ? "pointer-events-none" : ""
                }`}
              >
                <div className="pt-2">
                  <UnitRow
                    previews={u.files.map((f) => f.previewUrl)}
                    name={bulk.unitDisplayName(u)}
                    placeholder={bulk.defaultNameFor(idx)}
                    disabled={busy}
                    expandable={bulk.units.length > 1}
                    // En el panel de 440 tampoco entra el botón al lado del nombre: va
                    // la flecha que lo despliega, como en mobile.
                    directPick={!isMobile && !enPanel}
                    actionOpen={actionKey === u.key && !pickedKey}
                    galleryOpen={galleryKey === u.key && !pickedKey}
                    canAddMore={
                      u.files.length < MAX_IMAGENES_POR_PRODUCTO
                    }
                    isTarget={!!pickedKey && !hidden}
                    flashing={flashKey === u.key}
                    onTap={() => handleRowTap(u.key)}
                    onThumbTap={() => handleThumbTap(u.key)}
                    onAddImage={() => handleAddImageTo(u.key)}
                    onToggleAction={() => handleToggleAction(u.key)}
                    onPick={() => handlePickUp(u.key)}
                    onRename={(name) => bulk.renameUnit(u.key, name)}
                    onRemove={() => bulk.removeUnit(u.key)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const grillaTiendanube = (
    <>
      {/* ── Tiendanube (grid cuadrado seleccionable) ── */}
      {tnItems.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[13px] font-semibold text-[#16181D]">
              De Tiendanube · {tnSelected.size} de {selectableTn}{" "}
              seleccionado{tnSelected.size === 1 ? "" : "s"}
              {tnItems.length > MAX_TN && (
                <span className="font-normal text-black/40">
                  {" "}
                  · máx {MAX_TN} por vez
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={toggleAll}
              disabled={busy}
              className="text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-opacity disabled:opacity-40"
            >
              {allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
            </button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
            {tnItems.map((t) => (
              <TnGridCard
                key={t.id}
                item={t}
                selected={tnSelected.has(t.id)}
                disabled={busy}
                onCardClick={() => tnCardClick(t.id)}
                onPointerStart={(x, y, isTouch) =>
                  startTnDrag(t.id, x, y, isTouch)
                }
                onPointerCancel={cancelTnLongPress}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );

  // ── La grilla de productos de la pantalla y del pop-up de subir ─────────────
  // ADR-2026-10-14-varios-productos-en-grilla. Con fotos cargadas, la carga de varios se
  // ve como la de UN producto: en lugar de la zona de subida, una grilla de tarjetas
  // cuadradas (2 / 3 / 4 columnas, las mismas que las fotos de un producto) con las
  // acciones en círculos blancos arriba a la derecha, el nombre debajo y, al final, la
  // tarjeta punteada para sumar más. Las opciones del lote (agrupar con IA, nombres de
  // archivo) van como una barra chica junto al título, no como dos renglones a lo ancho.
  const grillaDeProductos = (
    <div>
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-0.5">
        <p className="text-[13px] font-semibold text-[#16181D]">
          Productos
          <span className="font-normal text-black/40"> · {localCount}</span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {bulk.itemsAgrupables.length > 1 && !aiNotice && (
            <button
              type="button"
              onClick={handleAgruparIa}
              disabled={busy}
              className={`inline-flex h-9 items-center gap-1.5 rounded-full border border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-white px-3.5 text-[13px] font-medium text-brand-primary transition-[border-color,box-shadow] hover:border-brand-primary hover:shadow-[0_10px_24px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] ${
                aiWorking ? "" : "disabled:opacity-50"
              }`}
            >
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                {aiWorking ? (
                  <SparklesLoaderInline className="size-4 text-brand-primary" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 text-brand-primary" />
                )}
              </span>
              {aiWorking ? "Agrupando…" : "Agrupar con IA"}
            </button>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={bulk.useFileNames}
            onClick={() => bulk.setUseFileNames(!bulk.useFileNames)}
            disabled={busy}
            className="inline-flex h-9 items-center gap-2 rounded-full border border-black/[0.08] bg-white pl-3.5 pr-2.5 text-[13px] font-medium text-black/65 transition-colors hover:border-black/[0.16] hover:text-[#16181D] disabled:opacity-50"
          >
            Nombres de archivo
            <span
              className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${bulk.useFileNames ? "bg-brand-primary" : "bg-neutral-300"}`}
            >
              <span
                className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${bulk.useFileNames ? "translate-x-3.5" : "translate-x-0.5"}`}
              />
            </span>
          </button>
        </div>
      </div>
      {aiNotice && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-2xl border border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-[rgb(var(--brand-primary-rgb)_/_0.05)] px-3.5 py-2.5">
          <span className="text-[12.5px] text-black/60">
            {aiNotice} Revisá y corregí lo que quieras antes de crear.
          </span>
          <button
            type="button"
            onClick={handleDeshacerIa}
            disabled={busy}
            className="shrink-0 text-[12.5px] font-medium text-brand-primary transition-opacity hover:opacity-70 disabled:opacity-40"
          >
            Deshacer
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        {bulk.units.map((u, idx) => (
          <TarjetaDeProductoDelLote
            key={u.key}
            previews={u.files.map((f) => f.previewUrl)}
            name={bulk.unitDisplayName(u)}
            // El nombre es un input no controlado: al cambiar de modo (nombres de
            // archivo sí/no) cambia de dónde sale, y la `key` lo vuelve a leer.
            nombreKey={`${u.key}-${bulk.useFileNames ? "archivo" : "plano"}`}
            placeholder={bulk.defaultNameFor(idx)}
            disabled={busy}
            puedeJuntar={bulk.units.length > 1}
            canAddMore={u.files.length < MAX_IMAGENES_POR_PRODUCTO}
            estado={
              pickedKey === u.key ? "levantada" : pickedKey ? "destino" : "normal"
            }
            flashing={flashKey === u.key}
            onTapDestino={() => handleRowTap(u.key)}
            onAddImage={() => handleAddImageTo(u.key)}
            onPick={() => handlePickUp(u.key)}
            onRename={(name) => bulk.renameUnit(u.key, name)}
            onRemove={() => bulk.removeUnit(u.key)}
          />
        ))}
        {/* Sumar más: la tarjeta punteada con el abanico, como "Agregar otra foto" de un
            producto. Cuadrada si cae sola en su fila; si comparte, se estira al alto de
            las tarjetas de producto. */}
        <button
          type="button"
          onClick={bulk.openFilePicker}
          disabled={busy || bulk.convertingHeic}
          {...soltar}
          className={`group relative flex aspect-square flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border-[1.5px] border-dashed px-3 text-center transition-[border-color,box-shadow] duration-300 disabled:cursor-not-allowed ${
            dragOver
              ? "border-brand-primary bg-[rgb(var(--brand-primary-rgb)_/_0.085)]"
              : "border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-white to-[rgb(var(--brand-primary-rgb)_/_0.055)] hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)]"
          }`}
        >
          <AbanicoDeFotos cargando={bulk.convertingHeic} />
          <span className="block">
            <span className="block text-[13px] font-semibold text-[#16181D]">
              {bulk.leyendoPdf ? "Leyendo el PDF…" : bulk.convertingHeic ? "Preparando las fotos…" : "Sumar más productos"}
            </span>
            <span className="mt-0.5 hidden text-[12px] text-black/45 sm:block">
              {dropEnabled ? "Hacé clic o arrastralas acá" : "Tocá para elegirlas"}
            </span>
          </span>
        </button>
      </div>
    </div>
  );

  const flotanteDeUnidad = (
    <>
      {/* ── Flotante de la unidad levantada: salió de la lista y espera que
          toques el producto destino. Entra con un fade + subida corta
          (keyframe `bulk-dock-in`, sutil a propósito). ── */}
      {(() => {
        const pickedIdx = pickedKey
          ? bulk.units.findIndex((u) => u.key === pickedKey)
          : -1;
        const picked = pickedIdx >= 0 ? bulk.units[pickedIdx] : null;
        if (!picked) return null;
        const cover = picked.files[0]?.previewUrl;
        return (
          <div className={`${sangria} pb-2 shrink-0`}>
            <div
              className="flex items-center gap-3 rounded-2xl border border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-white shadow-[0_12px_32px_-12px_rgb(var(--brand-primary-rgb)_/_0.35)] p-2.5"
              style={{ animation: "bulk-dock-in 0.25s ease-out" }}
            >
              <div className="relative w-11 h-11 rounded-xl overflow-hidden bg-black/[0.04] shrink-0">
                {cover ? (
                  <img
                    src={cover}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package className="w-5 h-5 text-black/25 m-auto" />
                )}
                {picked.files.length > 1 && (
                  <span className="absolute bottom-0.5 right-0.5 text-[10px] font-medium bg-black/60 text-white rounded px-1">
                    {picked.files.length}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span className="block text-[13.5px] font-semibold text-[#16181D] truncate">
                  {bulk.unitDisplayName(picked) ||
                    bulk.defaultNameFor(pickedIdx)}
                </span>
                <span
                  className={`block text-[12px] leading-snug ${
                    mergeError ? "text-error" : "text-black/50"
                  }`}
                >
                  {mergeError ??
                    (picked.files.length > 1
                      ? "Tocá el producto al que querés añadir estas imágenes"
                      : "Tocá el producto al que querés añadir esta imagen")}
                </span>
              </div>
              <button
                type="button"
                onClick={handleCancelPickup}
                aria-label="Cancelar"
                className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.05] text-black/50 hover:bg-black/[0.09] hover:text-black/80 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })()}
    </>
  );

  const avisos = (
    <>
      {notice && (
        <p className={`${sangria} pb-1 text-[13px] text-black/50`}>{notice}</p>
      )}
      {error && <p className={`${sangria} pb-1 text-[13px] text-error`}>{error}</p>}
      {/* Errores del hook (archivo no decodificable, tamaño excedido, formato
          bloqueado): sin esto, un archivo salteado por addFiles desaparecía
          de la tanda sin explicación — el modal solo mostraba su error local. */}
      {!error && bulk.error && (
        <p className={`${sangria} pb-1 text-[13px] text-error`}>{bulk.error}</p>
      )}
      {/* Lo que un .zip o carpeta dejó afuera sin ser error. Tarjeta con fondo y no texto
          suelto: en la pantalla los avisos van pegados abajo, encima de la grilla. */}
      {bulk.aviso && (
        <div className={`${sangria} pb-1`}>
          <div className="flex items-start justify-between gap-3 rounded-2xl border border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-[rgb(var(--brand-primary-rgb)_/_0.05)] px-3.5 py-2.5">
            <span className="text-[12.5px] leading-snug text-black/60">{bulk.aviso}</span>
            <button
              type="button"
              onClick={bulk.descartarAviso}
              aria-label="Cerrar aviso"
              className="shrink-0 text-black/40 transition-colors hover:text-black/70"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {bulk.saving && (
        <p className={`${sangria} pb-1 text-[13px] text-black/55`}>
          Creando {bulk.progress.done}/{bulk.progress.total}…
        </p>
      )}

      {/* Aviso de demora al traer de Tiendanube (la importación corre en
          background en n8n: descarga cada foto de la tienda → puede tardar
          unos minutos). Solo si hay productos de TN seleccionados. */}
      {tnSelected.size > 0 && (
        <div className={`${enPagina ? "" : "mx-6"} mb-2 flex items-start gap-2 rounded-2xl border border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-[rgb(var(--brand-primary-rgb)_/_0.05)] px-3.5 py-2.5`}>
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-brand-primary" />
          <p className="text-[12.5px] leading-snug text-black/60">
            {creating
              ? "Estamos trayendo tus productos de Tiendanube. Puede tardar unos minutos — se van sumando solos a tu catálogo."
              : "Traer productos de Tiendanube puede tardar unos minutos. Se van sumando solos a tu catálogo; podés cerrar esta ventana y seguir usando la app."}
          </p>
        </div>
      )}
    </>
  );

  const etiquetaDeCrear = creating
    ? "Creando…"
    : total > 0
      ? `Crear ${total} producto${total === 1 ? "" : "s"}`
      : "Crear productos";
  const pieClasico = (
    <>
      {/* Footer: píldoras, como el alta de un producto. En la hoja, "Crear" a todo el ancho
          (se cierra con la X) y el padding de abajo suma el safe-area para que el botón no
          quede bajo la barra de gestos del teléfono. */}
      <div
        className="px-6 py-4 flex items-center justify-end gap-2 border-t border-black/[0.06] shrink-0"
        style={
          isMobile
            ? { paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }
            : undefined
        }
      >
        {!isMobile && (
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="inline-flex h-10 items-center rounded-full px-4 text-[14px] font-medium text-black/55 transition-colors hover:bg-black/[0.04] hover:text-[#16181D] disabled:opacity-40"
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          onClick={handleCreate}
          disabled={!creatable || busy}
          className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-colors ${
            isMobile ? "h-11 flex-1 text-[15px]" : "h-10 px-5 text-[14px]"
          } ${
            !creatable || busy
              ? "cursor-not-allowed bg-black/[0.05] text-black/30"
              : "bg-brand-primary text-white shadow-[0_6px_16px_-6px_rgb(var(--brand-primary-rgb)_/_0.55)] hover:bg-brand-primary-hover"
          }`}
        >
          {creating && <Loader2 className="w-4 h-4 animate-spin" />}
          {etiquetaDeCrear}
        </button>
      </div>
    </>
  );

  // El contenido: cabecera, lista y pie. Es el MISMO con ventana propia y embebido —
  // lo único que cambia es qué lo envuelve (ADR-2026-09-22-el-alta-en-bloque-tambien-adentro-del-selector).
  const cuerpo = (
    <>
        {/* Pill de arrastre — señal visual de "esto es un sheet". Solo mobile, y sólo
            con ventana propia: embebido, la hoja es la del host. */}
        {isMobile && !embebido && !enPanel && (
          <div className="py-2 shrink-0">
            <div className="w-9 h-[5px] bg-black/[0.12] rounded-full mx-auto" />
          </div>
        )}
        {/* Header */}
        <div
          className={`px-6 pb-4 flex items-start justify-between gap-3 ${
            isMobile ? "pt-1" : "pt-6"
          }`}
        >
          <div className="min-w-0">
            {enPanel && (
              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                className="mb-3 inline-flex items-center gap-1 rounded-full bg-black/[0.05] py-1 pl-2 pr-3 text-[13px] font-medium text-black/60 transition-colors hover:bg-black/[0.09] hover:text-[#16181D] disabled:opacity-40"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Volver
              </button>
            )}
            <h2 className="text-[19px] font-semibold leading-tight tracking-[-0.01em] text-[#16181D]">
              Subir varios productos
            </h2>
            <p className="mt-1 text-[13.5px] leading-snug text-black/45">
              {dropEnabled
                ? "Arrastrá imágenes, subí archivos o traé tus productos de Tiendanube o Mercado Libre. Cada imagen es un producto distinto."
                : "Subí archivos o traé tus productos de Tiendanube o Mercado Libre. Cada imagen es un producto distinto."}
            </p>
          </div>
          {/* Embebido la salida es la del host (su flecha y su X): una tercera acá
              sería una salida de más, y encima con otro comportamiento. */}
          {!embebido && (!enPanel || onCerrarPanel) && (
            <button
              type="button"
              onClick={enPanel ? onCerrarPanel : handleClose}
              disabled={busy}
              aria-label="Cerrar"
              className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-full bg-black/[0.05] text-black/50 hover:bg-black/[0.09] hover:text-black/80 transition-colors disabled:opacity-40"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {inputsOcultos}
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          {zonaDeFuentes}
          {/* ── Archivos locales (lista con nombre editable) ── */}
          {bulk.items.length > 0 && (
            <>
              {/* En mobile la zona de arriba se reduce a dos botones (Subir
                  archivos / Traer de Tiendanube) y sin un divisor quedaban
                  tres botones apilados que parecen lo mismo: la línea separa
                  las FUENTES de las acciones sobre lo ya cargado. */}
              {isMobile && <div className="mt-5 border-t border-black/[0.06]" />}

              {opcionesDelLote}
              {listaDeUnidades}
            </>
          )}

          {grillaTiendanube}
        </div>

        {flotanteDeUnidad}
        {avisos}
        {pieClasico}
    </>
  );

  // El aviso de Tiendanube es una ventana aparte en los dos casos: se apila sobre la del
  // host con el `zIndex` que le pasen.
  // Aviso al conectar — MISMO modal (compartido) que el botón "Conectar" de Mi marca,
  // pero SIN la explicación (acá ya estás importando) → solo la nota multi-tienda + el
  // link de logout. Solo se dispara si la empresa ya tiene otra tienda (lo decide
  // `confirmNewStore` con company_has_stores).
  //
  // Delante van las explicaciones de "Conectar" de Mi marca, para cuando la tienda todavía no
  // está conectada (ADR-2026-10-22-subir-varios-conecta-con-la-misma-explicacion).
  const avisoDeTiendanube = (
    <>
      <TiendaNubeConnectModal
        open={tnExplicacion}
        companyHasStores={tnEmpresaConTiendas}
        onCancel={() => setTnExplicacion(false)}
        onConnect={() => {
          // Cierra y conecta en el mismo click: la ventana de Tiendanube se abre sincrónica.
          setTnExplicacion(false);
          void handleTraerTiendaNube(true);
        }}
        zIndex={zIndex + 10}
      />
      <MercadoLibreConnectModal
        open={mlExplicacion}
        reconectar={mlReconectar}
        conectando={mlConectando}
        error={mlErrorDeConexion}
        onCancel={() => setMlExplicacion(false)}
        onConnect={() => void conectarMercadoLibre()}
        zIndex={zIndex + 10}
      />
      <TiendaNubeConnectModal
        open={warnOpen}
        showExplanation={false}
        companyHasStores
        onCancel={() => {
          warnResolveRef.current?.(false);
          setWarnOpen(false);
        }}
        onConnect={() => {
          warnResolveRef.current?.(true);
          setWarnOpen(false);
        }}
        zIndex={zIndex + 10}
      />
    </>
  );

  const traerDeMercadoLibre = (
    <MercadoLibreImportModal
      open={mlOpen}
      companyId={companyId}
      brandId={brandId}
      onClose={() => setMlOpen(false)}
      onSaved={onSaved}
      zIndex={zIndex + 10}
    />
  );

  // ── En la pantalla "Subir productos" de Mi marca ──────────────────────────────
  // ADR-2026-10-13-subir-productos-es-una-pantalla. Las mismas piezas que la ventana
  // (fuentes, opciones del lote, lista, Tiendanube) sin su cabecera, y la barra de Crear
  // pegada abajo de la pantalla.
  if (enPagina) {
    // Para la cabecera de la pantalla (ver `onAcciones`).
    crearRef.current = handleCreate;
    accionesRef.current = {
      crear: crearEstable,
      puedeCrear: creatable && !busy,
      creando: creating,
      etiqueta: etiquetaDeCrear,
      hayCargado: bulk.items.length > 0 || tnSelected.size > 0,
    };
    // ADR-2026-10-13-varios-productos-reestructurado: la misma estructura que "Un solo
    // producto". Desde `md`, arriba y en fila, "Cómo funciona" y las tiendas; debajo, a
    // todo el ancho, la zona de subida y la lista de lo cargado. En el teléfono, las fotos
    // primero. "Crear" va en la cabecera de la pantalla en escritorio y en la barra fija
    // en el teléfono; en escritorio los avisos y el producto levantado quedan pegados
    // abajo, sin botones.
    return (
      <div className="flex flex-col motion-safe:animate-[aparecer-suave_0.3s_ease-out_both]">
        {inputsOcultos}
        <div className="flex flex-col gap-5 md:gap-6">
          <div className="order-2 grid grid-cols-1 gap-4 md:order-1 md:grid-cols-2 md:gap-5">
            {/* La tienda primero (a la izquierda en escritorio): es la otra forma de traer
                varios, más rápida que subir de a fotos. */}
            {/* Destacada a propósito: conectar la tienda es el camino que queremos que
                tomen (trae el catálogo entero de una), así que lleva el borde y el halo de
                marca, la etiqueta y el único botón relleno de la zona. */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[rgb(var(--brand-primary-rgb)_/_0.07)] via-white to-white px-4 py-4 shadow-[0_0_0_1.5px_rgb(var(--brand-primary-rgb)_/_0.35),0_14px_36px_-18px_rgb(var(--brand-primary-rgb)_/_0.5)] sm:px-5 sm:py-5">
              <span
                aria-hidden
                className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[radial-gradient(closest-side,rgb(var(--brand-primary-rgb)_/_0.16),transparent)]"
              />
              <div className="relative">
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <p className="text-[16px] font-semibold leading-tight text-[#16181D]">
                    Importá desde tu tienda
                  </p>
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-primary px-2 py-0.5 text-[11px] font-semibold text-white">
                    <Sparkles className="h-3 w-3" />
                    Recomendado
                  </span>
                </div>
                <p className="mb-4 text-[13px] leading-snug text-black/55">
                  Conectala una vez y traemos todo tu catálogo con fotos y nombres. Sin
                  subir nada a mano.
                </p>
                <div className="flex flex-wrap gap-2">{tiendas(true)}</div>
                <p className="mt-3 text-[12px] leading-snug text-black/40">
                  Puede tardar unos minutos.
                </p>
              </div>
            </div>
            {/* Sólo desde `md`: en el teléfono es una tarjeta más antes de llegar a subir, y
                lo que dice ya lo muestran la tienda de arriba y la zona de fotos. */}
            <div className={`hidden md:block ${TARJETA_DE_PAGINA}`}>
              <p className="mb-3 text-[14px] font-semibold text-[#16181D]">¿Cómo los subo?</p>
              <ul className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
                {["Desde tu tienda online", "Desde una carpeta", "Desde un .zip"].map((paso) => (
                  <li
                    key={paso}
                    // En el pop-up (980 de ancho), más angostos: con 18px el tercero bajaba.
                    className={`inline-flex h-[var(--btn-h)] shrink-0 items-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.07)] ${enVentana ? "px-3.5" : "px-[18px]"} text-[13.5px] font-medium text-brand-primary`}
                  >
                    {paso}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[12.5px] leading-snug text-black/45">
                ¿Varias fotos por producto? Subí un .zip con una carpeta por producto, o juntalas
                después con IA o a mano.
              </p>
            </div>
          </div>
          <div className="order-1 md:order-2">
            {/* Sin fotos, la zona de subida grande; con fotos, la grilla de productos
                (ADR-2026-10-14-varios-productos-en-grilla). */}
            {bulk.items.length > 0 ? grillaDeProductos : zonaDeFuentes}
            {grillaTiendanube}
            <div
              className={
                enVentana
                  ? "sticky bottom-0 z-10 mt-4 empty:hidden"
                  : "hidden lg:sticky lg:bottom-4 lg:z-10 lg:mt-4 lg:block"
              }
            >
              {flotanteDeUnidad}
              {avisos}
            </div>
          </div>
        </div>
        {alFinal}
        {!enVentana && (
        <PieFijoDeAlta
          hastaLg
          arriba={
            <>
              {flotanteDeUnidad}
              {avisos}
            </>
          }
        >
          <BotonCancelarDeAlta onClick={onClose} disabled={busy} />
          <BotonDeAlta onClick={handleCreate} disabled={!creatable || busy}>
            {creating && <Loader2 className="h-4 w-4 animate-spin" />}
            {etiquetaDeCrear}
          </BotonDeAlta>
        </PieFijoDeAlta>
        )}
        {avisoDeTiendanube}
        {traerDeMercadoLibre}
      </div>
    );
  }

  // ── En el panel lateral de "Agregar producto": una columna, sin ventana propia ──
  // ADR-2026-10-06-subir-varios-adentro-del-panel. Llena el contenedor del host (que ya
  // es la hoja / el panel flotante); los avisos de Tiendanube y Mercado Libre siguen
  // siendo ventanas, con el `zIndex` que pase el host.
  if (enPanel) {
    return (
      <div className="flex min-h-0 flex-1 flex-col motion-safe:animate-[aparecer-suave_0.3s_ease-out_both]">
        {cuerpo}
        {avisoDeTiendanube}
        {traerDeMercadoLibre}
      </div>
    );
  }

  // ── Embebido: la ventana del selector de fuentes (980), en DOS COLUMNAS ──────
  // ADR-2026-09-24-el-alta-en-bloque-tambien-en-dos-columnas. Misma anatomía que las
  // otras altas del selector (cabecera / dos columnas / barra de CTA): a la izquierda
  // lo que se hace —de dónde salen las imágenes y la lista de productos que se van a
  // crear—, a la derecha lo que se decide sobre el lote y lo que hay que saber.
  if (embebido) {
    const hayCargado = bulk.items.length > 0;
    return (
      <div className="h-full bg-white">
        <CuerpoDeAlta
          titulo="Subir varios productos"
          bajada={
            dropEnabled
              ? "Arrastrá imágenes, subí archivos o traé tus productos de Tiendanube o Mercado Libre. Cada imagen es un producto distinto."
              : "Subí archivos o traé tus productos de Tiendanube o Mercado Libre. Cada imagen es un producto distinto."
          }
        >
          {inputsOcultos}
          <ColumnasDeAlta>
            <ColumnaDeTrabajo>
              {zonaDeFuentes}
              {/* En el teléfono no hay columna de al lado: las decisiones del lote van
                  acá, arriba de la lista —que es donde estaban siempre—. En escritorio
                  se dibujan en el aside y esta copia no se monta. */}
              {hayCargado && (
                <div className="flex flex-col md:hidden">{opcionesDelLote}</div>
              )}
              {hayCargado && listaDeUnidades}
              {grillaTiendanube}
              {avisos}
            </ColumnaDeTrabajo>
            <AsideDeAlta titulo={hayCargado ? "Sobre este lote" : "Cómo funciona"}>
              {/* Las dos decisiones que valen para TODO el lote —agrupar y de dónde sale
                  el nombre— viven acá y no arriba de la lista: ahí se comían el alto que
                  necesita la lista, que es lo que el usuario vino a revisar. */}
              {hayCargado && (
                <div className="hidden flex-col md:flex">{opcionesDelLote}</div>
              )}
              <TipsDeAlta
                items={
                  hayCargado
                    ? [
                        "Tocá el nombre de un producto para cambiarlo.",
                        <>
                          Con <b>Añadir a otro producto</b> pasás esas imágenes a un
                          producto que ya está en la lista.
                        </>,
                        "Lo que crees acá queda en tu catálogo: después lo elegís desde Productos.",
                      ]
                    : [
                        "Cada imagen se crea como un producto distinto.",
                        "Si un producto tiene varias fotos, subí un .zip con una carpeta por producto (se llama como la carpeta), o subilas sueltas y agrupalas con IA o a mano.",
                        "Traer de Tiendanube puede tardar unos minutos; los productos se van sumando solos.",
                      ]
                }
              />
            </AsideDeAlta>
          </ColumnasDeAlta>
          {flotanteDeUnidad}
          <PieDeAlta>
            <BotonPrincipalDeAlta onClick={handleCreate} disabled={!creatable || busy}>
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              {creating
                ? "Creando…"
                : total > 0
                  ? `Crear ${total} producto${total === 1 ? "" : "s"}`
                  : "Crear productos"}
            </BotonPrincipalDeAlta>
          </PieDeAlta>
        </CuerpoDeAlta>
        {avisoDeTiendanube}
        {traerDeMercadoLibre}
      </div>
    );
  }

  return createPortal(
    <div
      className={`fixed inset-0 bg-black/55 flex justify-center transition-opacity duration-300 ease-out ${
        isMobile ? "items-end" : "items-center p-4"
      } ${animated ? "opacity-100" : "opacity-0"}`}
      style={{ zIndex }}
    >
      <div
        className={
          isMobile
            ? // Bottom sheet: pegado abajo, esquinas superiores redondeadas,
              // entra y sale deslizando (translateY 100% ↔ 0). El alto lo
              // limita el viewport menos el notch, así el header siempre queda
              // alcanzable.
              `bg-[#F9F9F9] rounded-t-[24px] shadow-[0_-12px_40px_-12px_rgba(16,24,40,0.3)] w-full max-h-[calc(100dvh-env(safe-area-inset-top,0px)-1rem)] flex flex-col overflow-hidden transition-transform duration-300 ease-out ${
                animated ? "translate-y-0" : "translate-y-full"
              }`
            : // Desktop: fade + un escalado mínimo. Sutil a propósito — una
              // modal centrada que se desliza se siente pesada.
              `bg-[#F9F9F9] rounded-[24px] shadow-[0_24px_64px_-16px_rgba(16,24,40,0.35)] ring-1 ring-black/[0.04] w-[min(640px,calc(100vw-32px))] max-h-[88vh] flex flex-col overflow-hidden transition-all duration-300 ease-out ${
                animated ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`
        }
      >
        {cuerpo}
      </div>

      {avisoDeTiendanube}
      {traerDeMercadoLibre}
    </div>,
    document.body,
  );
};

// ── Tarjeta cuadrada seleccionable (Tiendanube) ──
// Mapa best-effort de nombre de color (es) → hex, para los circulitos del grid.
// Lo que no matchea usa el gris neutro (el circulito igual comunica "hay colores").
const COLOR_HEX: Record<string, string> = {
  negro: "#111111", blanco: "#f5f5f5", gris: "#9ca3af", plata: "#c0c0c0",
  rojo: "#dc2626", bordo: "#7f1d1d", rosa: "#ec4899", naranja: "#f97316",
  amarillo: "#eab308", verde: "#16a34a", oliva: "#556b2f", celeste: "#38bdf8",
  azul: "#2563eb", marino: "#1e3a8a", violeta: "#7c3aed", lila: "#c4b5fd",
  marron: "#78350f", "marrón": "#78350f", beige: "#e7dcc8", crema: "#f3ead3",
  dorado: "#d4af37", camel: "#c19a6b",
};
const colorHex = (name: string): string =>
  COLOR_HEX[name.trim().toLowerCase()] ?? "#d1d5db";

const TnGridCard: React.FC<{
  item: TnProduct;
  selected: boolean;
  disabled?: boolean;
  onCardClick: () => void;
  onPointerStart: (clientX: number, clientY: number, isTouch: boolean) => void;
  onPointerCancel: () => void;
}> = ({
  item,
  selected,
  disabled,
  onCardClick,
  onPointerStart,
  onPointerCancel,
}) => (
  <button
    type="button"
    data-tn-id={item.id}
    draggable={false}
    onDragStart={(e) => e.preventDefault()}
    onClick={onCardClick}
    onMouseDown={(e) => {
      if (e.button === 0) onPointerStart(e.clientX, e.clientY, false);
    }}
    onTouchStart={(e) => {
      const t = e.touches[0];
      if (t) onPointerStart(t.clientX, t.clientY, true);
    }}
    onTouchMove={onPointerCancel}
    onTouchEnd={onPointerCancel}
    disabled={disabled}
    title={item.nombre}
    // Bloquea la selección/arrastre nativo de la imagen (copiar, menú "guardar
    // imagen" en mobile) para que no pelee con el drag-select.
    style={
      {
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      } as React.CSSProperties
    }
    className={`relative aspect-square rounded-2xl overflow-hidden bg-black/[0.04] ring-inset transition-shadow disabled:opacity-60 ${
      selected
        ? "ring-2 ring-brand-primary"
        : "ring-1 ring-black/10 hover:ring-black/20"
    }`}
  >
    {item.img_src ? (
      <img
        src={item.img_src}
        alt=""
        loading="lazy"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
      />
    ) : (
      <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
        <Package className="w-5 h-5 text-black/25" strokeWidth={1.5} />
      </div>
    )}
    {selected && (
      <div className="absolute inset-0 bg-[rgb(var(--brand-primary-rgb)_/_0.15)] pointer-events-none" />
    )}
    <span
      className={`absolute top-2 right-2 z-[1] w-6 h-6 rounded-full inline-flex items-center justify-center pointer-events-none transition-colors ${
        selected
          ? "bg-brand-primary text-white ring-2 ring-white"
          : "bg-white/90 ring-1 ring-black/10 text-transparent shadow-sm"
      }`}
    >
      <Check className="w-3.5 h-3.5" strokeWidth={2.75} />
    </span>
    {item.variantes && item.variantes.length > 1 && (
      <span className="absolute top-1.5 left-1.5 flex items-center gap-0.5 pointer-events-none">
        {(item.colores && item.colores.length > 0
          ? item.colores
          : item.variantes.map((v) => v.nombre)
        )
          .slice(0, 5)
          .map((c, i) => (
            <span
              key={i}
              className="w-2.5 h-2.5 rounded-full ring-1 ring-white/80 shadow-sm"
              style={{ backgroundColor: item.colores ? colorHex(c) : "#d1d5db" }}
            />
          ))}
      </span>
    )}
    <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/65 to-transparent px-1.5 pt-4 pb-1 text-white text-[11px] leading-tight line-clamp-2 text-left pointer-events-none select-none">
      {item.nombre}
    </span>
  </button>
);

// Entrada escalonada de los thumbnails de la galería. Solo corre con la
// galería abierta: al cerrar se quita la animación y reasignarla en la
// próxima apertura la reinicia (los thumbs quedan montados siempre para que
// el colapso de altura tenga contenido durante la salida). `both` los
// mantiene invisibles hasta que les toca su delay.
const thumbAnim = (open: boolean, index: number): React.CSSProperties =>
  open
    ? { animation: `bulk-thumb-in 0.25s ease-out ${index * 0.05}s both` }
    : { animation: "none" };

// ── Fila de una unidad local (1 producto = 1+ imágenes) ──
// Unifica las viejas ItemRow (item suelto) y GroupRow (grupo por filename):
// con los merges manuales cualquier fila puede tener varias imágenes, sea
// cual sea el modo. El nombre se edita tocándolo directo (no hay lapicito);
// el chevron de la derecha despliega la opción "Añadir a otro producto".
// El input de nombre va NO controlado a propósito: en modo stem, vaciarlo
// hace caer el display al baseName y un input controlado "saltaría" a ese
// fallback mientras tipeás.
const UnitRow: React.FC<{
  /** previewUrls de TODAS las imágenes de la unidad; la primera es la
   *  portada (imagen principal). */
  previews: string[];
  name: string;
  placeholder: string;
  disabled?: boolean;
  /** Hay 2+ productos: "añadir a otro" tiene destino posible. Con uno solo
   *  no se ofrece (ni chevron ni botón directo). */
  expandable: boolean;
  /** Desktop: el "Añadir a otro producto" va como botón directo junto al de
   *  eliminar, sin chevron ni panel desplegable (eso queda para mobile,
   *  donde un ícono más apretaría la fila). */
  directPick: boolean;
  /** La opción "Añadir a otro producto" está desplegada bajo esta fila. */
  actionOpen: boolean;
  /** Galería abierta (tap en el thumbnail): el nombre se oculta y se ven las
   *  demás imágenes + el "+" para añadir otra. */
  galleryOpen: boolean;
  /** La unidad todavía no llegó al tope de imágenes por producto. */
  canAddMore: boolean;
  /** Hay otra unidad levantada: esta fila es un destino tocable. */
  isTarget: boolean;
  /** Acaba de recibir imágenes → flash breve que se desvanece solo. */
  flashing: boolean;
  onTap: () => void;
  onThumbTap: () => void;
  onAddImage: () => void;
  onToggleAction: () => void;
  onPick: () => void;
  onRename: (name: string) => void;
  onRemove: () => void;
}> = ({
  previews,
  name,
  placeholder,
  disabled,
  expandable,
  directPick,
  actionOpen,
  galleryOpen,
  canAddMore,
  isTarget,
  flashing,
  onTap,
  onThumbTap,
  onAddImage,
  onToggleAction,
  onPick,
  onRename,
  onRemove,
}) => {
  const [editing, setEditing] = React.useState(false);
  const cover = previews[0];
  const count = previews.length;
  // La galería reemplaza al nombre: si estaba en edición, se cierra (que el
  // input no "reaparezca" enfocado al cerrar la galería).
  React.useEffect(() => {
    if (galleryOpen) setEditing(false);
  }, [galleryOpen]);
  return (
    <div
      onClick={onTap}
      // Marca "esta fila tiene la galería abierta" para el listener global
      // de tap-afuera del modal.
      data-galeria-abierta={galleryOpen ? "" : undefined}
      // Los tintes van con hex+alfa arbitrario y no como brand-*/NN: esas
      // clases no compilan (el color de marca es una CSS var, no un triplete
      // RGB). El flash es un keyframe one-shot y no una transition, para no
      // arrastrar la duración del hover.
      className={`border rounded-2xl bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors ${
        isTarget
          ? "cursor-pointer border-black/[0.08] hover:border-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.05)]"
          : "border-black/[0.08]"
      }`}
      style={
        flashing ? { animation: "bulk-flash 0.6s ease-out" } : undefined
      }
    >
      <div className="flex items-center gap-3 p-2.5">
        <button
          type="button"
          title={isTarget ? undefined : "Ver imágenes del producto"}
          onClick={(e) => {
            // Con una unidad levantada el tap burbujea a la fila (merge);
            // sin movimiento activo, alterna la galería.
            if (isTarget) return;
            e.stopPropagation();
            onThumbTap();
          }}
          disabled={disabled}
          // La portada CRECE con la card (48→64px): mismo ritmo que la
          // expansión de altura. Transition inline y no clase arbitraria —
          // criterio del proyecto para clases dudosas de Tailwind.
          style={{ transition: "width 0.3s ease-out, height 0.3s ease-out" }}
          className={`relative rounded-xl overflow-hidden bg-black/[0.04] shrink-0 ${
            galleryOpen ? "w-16 h-16" : "w-12 h-12"
          }`}
        >
          {cover ? (
            <img src={cover} alt="" className="w-full h-full object-cover" />
          ) : (
            <Package className="w-5 h-5 text-black/25 m-auto" />
          )}
          {count > 1 && (
            // Con la galería abierta las imágenes están a la vista: el
            // contador sobra y se desvanece.
            <span
              className={`absolute bottom-1 right-1 text-[10px] font-medium bg-black/55 text-white rounded-full px-1.5 backdrop-blur-sm transition-opacity duration-200 ${
                galleryOpen ? "opacity-0" : "opacity-100"
              }`}
            >
              {count}
            </span>
          )}
        </button>
        <div className="flex-1 min-w-0">
          {/* Capa nombre: se pliega mientras la galería se despliega. */}
          <div
            className="grid transition-all duration-300 ease-out"
            style={{
              gridTemplateRows: galleryOpen ? "0fr" : "1fr",
              opacity: galleryOpen ? 0 : 1,
            }}
          >
            <div
              className={`min-h-0 overflow-hidden ${
                galleryOpen ? "pointer-events-none" : ""
              }`}
            >
              {editing ? (
                <input
                  autoFocus
                  defaultValue={name}
                  placeholder={placeholder}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onRename(e.target.value)}
                  onBlur={() => setEditing(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setEditing(false);
                  }}
                  className="w-full px-3 py-1 rounded-full border border-black/[0.08] bg-white text-[14px] text-[#16181D] focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.085)] outline-none transition-shadow"
                />
              ) : (
                <span
                  onClick={(e) => {
                    // Con una unidad levantada la fila entera es destino del
                    // merge: el nombre no captura el tap.
                    if (isTarget || disabled) return;
                    e.stopPropagation();
                    setEditing(true);
                  }}
                  title={isTarget || disabled ? undefined : "Editar nombre"}
                  className={`block text-[14px] font-medium truncate ${
                    name ? "text-[#16181D]" : "text-black/40"
                  } ${isTarget || disabled ? "" : "cursor-text"}`}
                >
                  {name || placeholder}
                </span>
              )}
            </div>
          </div>
          {/* Capa galería: la card se AMPLÍA — el nombre (una línea) se
              pliega mientras esta capa (thumbs de 64px) se despliega, así la
              altura anima suave en ambos sentidos. Las DEMÁS imágenes del
              producto (la portada ya está a la izquierda) + el "+" para
              añadir otra. Montada siempre para que el colapso de salida tenga
              contenido. */}
          <div
            className="grid transition-all duration-300 ease-out"
            style={{
              gridTemplateRows: galleryOpen ? "1fr" : "0fr",
              opacity: galleryOpen ? 1 : 0,
            }}
          >
            {/* px-2.5/-mx-2.5 (acá Y en el scroller): tanto este
                overflow-hidden como el overflow-x-auto recortan en el borde
                de su padding-box — sin aire en AMBOS, la sombra del
                primer/último thumb quedaba cortada a ras. El padding da lugar
                de pintado y el margen negativo lo compensa para que los
                thumbs sigan alineados con el nombre (caben en el p-2.5 de la
                fila). */}
            <div
              className={`min-h-0 overflow-hidden px-2.5 -mx-2.5 ${
                galleryOpen ? "" : "pointer-events-none"
              }`}
            >
              <div className="flex items-center gap-2 overflow-x-auto py-2.5 px-2.5 -mx-2.5">
                {/* Los thumbs también crecen 48→64px al abrir (y se encogen
                    al cerrar): el tamaño transiciona junto con la card,
                    encima del fade escalonado de entrada. */}
                {previews.slice(1).map((src, i) => (
                  <div
                    key={i}
                    style={{
                      ...thumbAnim(galleryOpen, i),
                      transition:
                        "width 0.3s ease-out, height 0.3s ease-out",
                    }}
                    className={`rounded-xl overflow-hidden bg-black/[0.04] shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.18)] ${
                      galleryOpen ? "w-16 h-16" : "w-12 h-12"
                    }`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                {canAddMore && (
                  <button
                    type="button"
                    disabled={disabled}
                    title="Añadir otra imagen a este producto"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddImage();
                    }}
                    style={{
                      ...thumbAnim(galleryOpen, previews.length - 1),
                      // El tamaño acompaña a la card; el hover sigue rápido.
                      transition:
                        "width 0.3s ease-out, height 0.3s ease-out, background-color 0.15s ease, color 0.15s ease",
                    }}
                    className={`rounded-xl border-[1.5px] border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-[rgb(var(--brand-primary-rgb)_/_0.05)] hover:border-brand-primary shrink-0 inline-flex items-center justify-center text-brand-primary disabled:opacity-40 ${
                      galleryOpen ? "w-16 h-16" : "w-12 h-12"
                    }`}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <RowActions
          showPick={directPick && expandable && !isTarget}
          showExpand={!directPick && expandable && !isTarget}
          expanded={actionOpen}
          disabled={disabled}
          onToggleExpand={onToggleAction}
          onPick={onPick}
          onRemove={onRemove}
        />
      </div>
      {/* Opción "Añadir a otro producto" (solo mobile: en desktop va como
          botón directo en las acciones): se despliega bajo la fila con el
          mismo colapso de altura que usa la lista. Montada siempre para que
          la transición corra en ambos sentidos. */}
      {!directPick && (
        <div
          className="grid transition-all duration-200 ease-out"
          style={{
            gridTemplateRows: actionOpen ? "1fr" : "0fr",
            opacity: actionOpen ? 1 : 0,
          }}
        >
          <div
            className={`min-h-0 overflow-hidden ${
              actionOpen ? "" : "pointer-events-none"
            }`}
          >
            <div className="px-2.5 pb-2.5">
              <button
                type="button"
                disabled={disabled}
                tabIndex={actionOpen ? 0 : -1}
                onClick={(e) => {
                  e.stopPropagation();
                  onPick();
                }}
                className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-full border border-black/[0.08] bg-white hover:border-black/[0.16] text-[13px] font-medium text-[#16181D] transition-colors disabled:opacity-40"
              >
                <ImagePlus className="w-3.5 h-3.5" />
                Añadir a otro producto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RowActions: React.FC<{
  /** Desktop: botón directo "Añadir a otro producto" junto al de eliminar. */
  showPick: boolean;
  /** Mobile: chevron que despliega el panel con esa misma opción. Ambos se
   *  ocultan con un solo producto o en modo destino de merge. */
  showExpand: boolean;
  expanded: boolean;
  disabled?: boolean;
  onToggleExpand: () => void;
  onPick: () => void;
  onRemove: () => void;
}> = ({
  showPick,
  showExpand,
  expanded,
  disabled,
  onToggleExpand,
  onPick,
  onRemove,
}) => (
  // stopPropagation: la fila entera es tocable (destino de merge) y estos
  // botones no deben disparar ese tap.
  <>
    {showPick && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPick();
        }}
        disabled={disabled}
        className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-black/[0.08] bg-white text-[12.5px] font-medium text-black/65 hover:border-black/[0.16] hover:text-[#16181D] whitespace-nowrap transition-colors disabled:opacity-40"
      >
        <ImagePlus className="w-3.5 h-3.5" />
        Añadir a otro producto
      </button>
    )}
    {showExpand && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand();
        }}
        disabled={disabled}
        title="Más opciones"
        aria-expanded={expanded}
        className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full text-black/40 hover:bg-black/[0.05] hover:text-black/70 transition-colors disabled:opacity-40"
      >
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
    )}
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      disabled={disabled}
      title="Quitar"
      className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full text-black/40 hover:bg-error-soft hover:text-error transition-colors disabled:opacity-40"
    >
      <Trash2 className="w-4 h-4" />
    </button>
  </>
);

/**
 * Un producto del lote en la grilla de la pantalla / el pop-up de subir
 * (ADR-2026-10-14-varios-productos-en-grilla): la misma tarjeta que una foto del alta de
 * un producto —cuadrada, con las acciones en círculos blancos arriba a la derecha— más el
 * nombre editable debajo. Si el producto junta varias fotos, las otras asoman abajo a la
 * izquierda como miniaturas.
 *
 * "Juntar con otro" levanta el producto (`estado="levantada"`) y deja a los demás como
 * destino (`"destino"`): tocar uno los junta. Es el "Añadir a otro producto" de la lista.
 */
const TarjetaDeProductoDelLote: React.FC<{
  previews: string[];
  name: string;
  nombreKey: string;
  placeholder: string;
  disabled: boolean;
  puedeJuntar: boolean;
  canAddMore: boolean;
  estado: "normal" | "levantada" | "destino";
  flashing: boolean;
  onTapDestino: () => void;
  onAddImage: () => void;
  onPick: () => void;
  onRename: (name: string) => void;
  onRemove: () => void;
}> = ({
  previews,
  name,
  nombreKey,
  placeholder,
  disabled,
  puedeJuntar,
  canAddMore,
  estado,
  flashing,
  onTapDestino,
  onAddImage,
  onPick,
  onRename,
  onRemove,
}) => {
  // La foto grande: la que se toque en la tira (sólo la vista previa; la principal que se
  // guarda sigue siendo la primera). Si el producto pierde fotos, vuelve a una que exista.
  const [vista, setVista] = React.useState(0);
  // El nombre, controlado: lo puede cambiar alguien más que el usuario (la IA al agrupar,
  // "Deshacer", el modo nombres de archivo) aunque la tarjeta no se vuelva a montar. Toma
  // el de afuera cuando cambia, salvo mientras se está escribiendo en el campo.
  const nombreRef = React.useRef<HTMLInputElement>(null);
  const [texto, setTexto] = React.useState(name);
  React.useEffect(() => {
    if (document.activeElement !== nombreRef.current) setTexto(name);
  }, [name]);
  const indiceVisto = vista < previews.length ? vista : 0;
  const cover = previews[indiceVisto];
  const destino = estado === "destino";
  const levantada = estado === "levantada";
  // Las acciones de arriba a la derecha. "+ Foto" y "Agrupar" llevan su etiqueta desde `md`
  // (ADR-2026-10-14-varios-productos-en-grilla-2): en el teléfono, a dos columnas, la foto
  // mide ~150px y las tres pastillas no entran — ahí quedan como círculos.
  const accion =
    "inline-flex h-8 min-w-8 items-center justify-center gap-1 rounded-full bg-white/95 text-[12.5px] font-medium text-black/75 shadow-md ring-1 ring-black/[0.06] backdrop-blur-md transition-all hover:bg-white active:scale-95 disabled:opacity-40";
  return (
    <div
      onClick={destino ? onTapDestino : undefined}
      className={`group relative flex min-w-0 flex-col rounded-2xl bg-white p-1.5 transition-shadow duration-200 motion-safe:animate-[aparecer_0.45s_ease-out_both] ${
        levantada
          ? "shadow-[0_0_0_2px_var(--brand-primary),0_12px_28px_-14px_rgb(var(--brand-primary-rgb)_/_0.5)]"
          : destino
            ? "cursor-pointer shadow-[0_1px_2px_rgba(16,24,40,0.05),0_0_0_1px_rgba(16,24,40,0.06)] hover:shadow-[0_0_0_2px_var(--brand-primary)]"
            : "shadow-[0_1px_2px_rgba(16,24,40,0.05),0_0_0_1px_rgba(16,24,40,0.06)]"
      }`}
      style={flashing ? { animation: "bulk-flash 0.6s ease-out" } : undefined}
    >
      <div className="relative aspect-square overflow-hidden rounded-xl bg-black/[0.04]">
        {cover ? (
          <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <Package className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-black/25" />
        )}
        <span aria-hidden className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-black/10" />

        {/* Con más de una foto, TODAS como miniaturas ADENTRO de la grande, abajo, sobre un
            degradé que las separa de la foto (ADR-2026-10-14-varios-tira-adentro-de-la-foto).
            Tocar una la muestra grande (borde azul = la que se ve). Entran cinco (el tope);
            con fotos de más la tira se desliza de costado, las que sobran van atenuadas y
            entran cinco y media para que la que asoma diga que sigue. El `p-1` es lugar para
            el borde: el scroll recorta en su caja. Juntando, no captura el toque. */}
        {previews.length > 1 && (
          <>
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent"
            />
            <div
              className={`absolute inset-x-1 bottom-1 flex gap-1.5 overflow-x-auto p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
                estado === "normal" ? "" : "pointer-events-none"
              }`}
            >
              {previews.map((src, i) => {
                const sobra = i >= MAX_IMAGENES_POR_PRODUCTO;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={estado === "normal" ? () => setVista(i) : undefined}
                    aria-label={`Ver foto ${i + 1} de ${previews.length}${sobra ? " (no se sube)" : ""}`}
                    aria-pressed={i === indiceVisto}
                    title={sobra ? "No se sube: pasa el máximo de fotos por producto" : undefined}
                    className={`relative aspect-square max-w-[40px] shrink-0 overflow-hidden rounded-md shadow-[0_2px_6px_rgba(0,0,0,0.25)] transition-shadow ${
                      previews.length > MAX_IMAGENES_POR_PRODUCTO ? "w-[16%]" : "w-[17%]"
                    } ${
                      i === indiceVisto
                        ? "ring-2 ring-brand-primary"
                        : "ring-2 ring-white hover:ring-[rgb(var(--brand-primary-rgb)_/_0.3)]"
                    }`}
                  >
                    <img
                      src={src}
                      alt=""
                      className={`h-full w-full object-cover ${sobra ? "opacity-40 grayscale" : ""}`}
                    />
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Acciones, como las de una foto del alta de un producto. Siempre visibles: en
            el teléfono no hay hover. Mientras se junta, se van. */}
        {estado === "normal" && (
          <div className="absolute right-2 top-2 flex gap-1.5">
            {canAddMore && (
              <button
                type="button"
                onClick={onAddImage}
                disabled={disabled}
                title="Sumar otra foto a este producto"
                aria-label="Sumar otra foto a este producto"
                className={`${accion} hover:text-brand-primary md:pl-2 md:pr-2.5`}
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Foto</span>
              </button>
            )}
            {puedeJuntar && (
              <button
                type="button"
                onClick={onPick}
                disabled={disabled}
                title="Agrupar con otro producto"
                aria-label="Agrupar con otro producto"
                className={`${accion} hover:text-brand-primary md:pl-2 md:pr-2.5`}
              >
                <Combine className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Agrupar</span>
              </button>
            )}
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              title="Quitar producto"
              aria-label="Quitar producto"
              className={`${accion} w-8 hover:text-error`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Juntando: el levantado se apaga; los demás dicen dónde tocar. */}
        {levantada && (
          <span className="absolute inset-0 flex items-center justify-center bg-white/55 backdrop-blur-[1px]">
            <span className="rounded-full bg-brand-primary px-2.5 py-1 text-[11.5px] font-semibold text-white shadow-md">
              Elegí con cuál
            </span>
          </span>
        )}
        {destino && (
          <span className="absolute inset-0 flex items-center justify-center bg-[rgb(var(--brand-primary-rgb)_/_0)] transition-colors group-hover:bg-[rgb(var(--brand-primary-rgb)_/_0.12)]">
            <span className="rounded-full bg-white px-2.5 py-1 text-[11.5px] font-semibold text-brand-primary shadow-md">
              Agrupar acá
            </span>
          </span>
        )}
      </div>

      {/* Con fotos de más (el agrupado por nombre de archivo no tiene tope), el aviso de
          cuántas no se suben — ADR-2026-10-14-varios-mas-de-cinco-fotos. */}
      {previews.length > MAX_IMAGENES_POR_PRODUCTO && (
        <p className="mt-1.5 px-0.5 text-[12px] leading-snug text-[#B54708]">
          Máximo {MAX_IMAGENES_POR_PRODUCTO} fotos por producto:{" "}
          {previews.length - MAX_IMAGENES_POR_PRODUCTO === 1
            ? "1 no se sube"
            : `${previews.length - MAX_IMAGENES_POR_PRODUCTO} no se suben`}
          .
        </p>
      )}

      {/* El nombre: un campo de verdad (borde y fondo, como el nombre del alta de un
          producto), para que se vea que se escribe ahí. `mt-auto` lo baja al pie, así en
          una fila con y sin tira de fotos los nombres quedan alineados. El lápiz va desde
          `sm`: en el teléfono los inputs son de 16px (para que iOS no haga zoom). El texto de
          ejemplo sí va chico (13px): la regla de `index.css` es para el input y el zoom
          depende de lo que se escribe, no del placeholder — a 16px "Nuevo producto 42" no
          entraba y se cortaba. */}
      <label className="relative mt-auto block pt-1.5">
        <input
          key={nombreKey}
          ref={nombreRef}
          value={texto}
          placeholder={placeholder}
          onChange={(e) => {
            setTexto(e.target.value);
            onRename(e.target.value);
          }}
          // Al salir, el que quedó de verdad (en modo nombres de archivo, borrarlo vuelve
          // al nombre del archivo).
          onBlur={() => setTexto(name)}
          disabled={disabled || estado !== "normal"}
          aria-label="Nombre del producto"
          className="peer h-9 w-full truncate rounded-xl border border-black/[0.08] bg-[#F7F8FA] pl-2.5 pr-2 text-[13px] font-medium text-[#16181D] outline-none transition-[border-color,box-shadow,background-color] placeholder:text-[13px] placeholder:text-black/40 hover:border-black/[0.16] sm:placeholder:text-[13.5px] focus:border-brand-primary focus:bg-white focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.085)] disabled:cursor-default disabled:hover:border-black/[0.08] sm:pl-3 sm:pr-8 sm:text-[13.5px]"
        />
        <Pencil
          aria-hidden
          className="pointer-events-none absolute right-3 top-[calc(50%+3px)] hidden h-3.5 w-3.5 -translate-y-1/2 text-black/35 peer-focus:opacity-0 sm:block"
        />
      </label>
    </div>
  );
};

export default AgregarVariosModal;
